import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LockIcon, SparkleIcon, CheckCircleIcon, WarningCircleIcon, TrashIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";

import FormattedNumberInput from "@/components/FormattedNumberInput";
import MachineTimelinePeek from "./MachineTimelinePeek";
const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] text-white h-9";

// ---------- Planning Calculator ----------
// Two-stage sizing tool:
//   Stage 1 · Source — pick a Sales Order and Article. Order Qty auto-fills from the SO;
//     picks/cm, speed, efficiency auto-fill from the Product Master (Items). Formula
//     `Speed / Picks-per-cm × 60 / 100` gives theoretical Production per Hour per Head
//     (m/hr). Multiplied by Heads × Hours × Efficiency% → Production per Day per Machine.
//   Stage 2 · Order-vs-Machine — shop-floor spreadsheet:
//     Required Prod/Day = Order Qty / Delivery Days
//     Machines Required = Required/Day / Prod-per-Machine
//     Allotted Capacity = Available Machines × Prod-per-Machine
//     Actual Days = Order Qty / Allotted Capacity
export default function PlanningCalculator({ orders = [], items = [] }) {
  const [orderQty, setOrderQty] = useState(200000);
  const [deliveryDays, setDeliveryDays] = useState(30);
  const [prodPerMachine, setProdPerMachine] = useState(2700);
  const [availableMachines, setAvailableMachines] = useState(3);

  // Feb 2026 · Export SOs are pull-able for capacity planning too. Loaded
  // once and merged into a combined list so the existing picker + line-item
  // grid + allocation logic work unchanged. Export rows are flagged so the
  // dropdown renders an [EXPORT] chip and freezeCapacity can persist the
  // right kind of link.
  const [esoOrders, setEsoOrders] = useState([]);
  const [sourceKind, setSourceKind] = useState("domestic"); // "domestic" | "export"
  const [productionOrders, setProductionOrders] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [selectedArticle, setSelectedArticle] = useState("");
  const [selectedMachineTypeId, setSelectedMachineTypeId] = useState("");
  const [machineTypes, setMachineTypes] = useState([]);
  const [machines, setMachines] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedMachineIds, setSelectedMachineIds] = useState(new Set());
  // Per-machine allocation override. If unset for a machine, falls back to the
  // even-split of Order Qty across the currently selected count.
  const [perMachineQty, setPerMachineQty] = useState({});
  // Per-machine start-date override (Feb 2026). Populated when the planner
  // clicks "Queue after" on an Occupied row → sets that machine's start to
  // the day AFTER the blocking plan finishes so the new order queues
  // back-to-back on the same loom. Falls back to the global `startDate`.
  const [perMachineStart, setPerMachineStart] = useState({});
  // Per-machine head-split (Feb 2026). Structure:
  //   { [machine_id]: [ { item_id, article_no, heads, qty }, ... ] }
  // Populated when the planner enables "Split heads" on a selected loom and
  // adds multiple article/colour variants that should run in parallel on the
  // SAME machine (e.g. 8-head jacquard running 2 heads of variant-01,
  // 4 heads of variant-02 and 2 heads of variant-03 simultaneously). When a
  // machine has splits, freezeCapacity creates one production plan per
  // variant, each carrying its own item_id, planned_qty and heads_used.
  const [perMachineSplits, setPerMachineSplits] = useState({});
  // Feb 2026 · Multi-WO Split-Head Allocation — planner can attach
  // additional Work Orders to the calculator once a primary WO is
  // picked, BUT only when the extra WO shares the same Width/Article
  // signature (`Width / Article No` — tokens 2 and 3 of the product
  // code path, e.g. `RNJ/38/10547/…` → `38/10547`). This mirrors the
  // physical constraint that only two SKUs sharing the same fabric
  // width and base article can run on adjacent heads of the same loom.
  const [additionalPoIds, setAdditionalPoIds] = useState(new Set());
  const [additionalPoErr, setAdditionalPoErr] = useState("");
  // Extract the "Width/Article" key from a product code. Splits on
  // `/`, returns tokens 2 and 3 joined by `/`. Blanks return "".
  const widthArticleKeyOf = (code) => {
    if (!code) return "";
    const parts = String(code).split("/").map(s => s.trim());
    return parts.length >= 3 ? `${parts[1]}/${parts[2]}` : "";
  };
  const productCodeOfWo = (po) => (po?.item_code) || (po?.items?.[0]?.item_code) || (po?.items?.[0]?.article_no) || "";
  const [freezing, setFreezing] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [heads, setHeads] = useState(8);
  const [hoursPerDay, setHoursPerDay] = useState(22);
  // Time-phase inputs (hours). Defaults mirror typical narrow-fabric shop-floor norms
  // — sensible starting values while still user-editable per allocation.
  const [changeoverHours, setChangeoverHours] = useState(2);
  const [setupHours, setSetupHours] = useState(4);

  // Load Machine Types and Machines masters once for the availability picker.
  useEffect(() => {
    api.get("/machine-types/").then(r => setMachineTypes(r.data)).catch(() => {});
    api.get("/machines/").then(r => setMachines(r.data)).catch(() => {});
    // Feb 2026 · Fetch Export SOs so planners can size capacity for exports too.
    // Map to the SO shape the rest of the calculator expects.
    api.get("/exports/orders/").then(r => {
      const mapped = (r.data || []).map(e => ({
        id: e.id,
        order_no: e.doc_no,
        customer_name: e.buyer_name,
        customer: e.buyer_name,
        po_number: e.buyer_po_no,
        order_date: e.order_date,
        delivery_date: e.delivery_date,
        items: (e.items || []).map(li => ({
          article_no: li.article_no,
          order_qty: +(li.qty || 0),
          qty: +(li.qty || 0),
          item_no: li.item_no,
          style_no: li.style_no,
          hs_code: li.hs_code,
        })),
        _is_export: true,
      }));
      setEsoOrders(mapped);
    }).catch(() => {});
    // Approved Work Orders only — Draft WOs aren't ready for planning,
    // Cancelled ones no longer consume capacity.
    api.get("/work-orders/").then(r => {
      setProductionOrders((r.data || []).filter(p => p.status === "Approved"));
    }).catch(() => {});
    reloadPlans();
  }, []);
  const reloadPlans = () => {
    api.get("/production-plans/").then(r => setPlans(r.data)).catch(() => {});
  };
  // Feb 2026 · Universal Over-Allocation Guard — "Recalculate Balance"
  // action. Pulls plans AND work orders fresh so any stale double-count
  // (e.g. a plan created in another tab / a partial release that didn't
  // reflect) instantly clears from the Order/Alloc/Balance grid.
  const [recalculating, setRecalculating] = useState(false);
  const recalculateBalance = async () => {
    setRecalculating(true);
    try {
      const [wo, pl] = await Promise.all([
        api.get("/work-orders/"),
        api.get("/production-plans/"),
      ]);
      setProductionOrders((wo.data || []).filter(p => p.status === "Approved"));
      setPlans(pl.data || []);
      toast.success("Balance recalculated — Order / Alloc / Balance refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recalculate failed");
    } finally {
      setRecalculating(false);
    }
  };

  const selectedMachineType = useMemo(
    () => machineTypes.find(m => m.id === selectedMachineTypeId) || null,
    [machineTypes, selectedMachineTypeId]
  );
  // Cascade heads from the picked Machine Model.
  useEffect(() => {
    if (selectedMachineType && +selectedMachineType.no_of_heads > 0) {
      setHeads(selectedMachineType.no_of_heads);
    }
  }, [selectedMachineType]);

  const selectedOrder = useMemo(() => {
    // Feb 2026 · look up across both SO + ESO pools so the picker can be
    // switched to Export without losing the previously-selected order.
    const combined = [...orders, ...esoOrders];
    return combined.find(o => o.id === selectedOrderId) || null;
  }, [orders, esoOrders, selectedOrderId]);
  const domesticOrders = useMemo(() => orders.filter(o => !o._is_export && o.order_against !== "stock"), [orders]);
  const nonStockEso = useMemo(() => esoOrders.filter(o => o.order_against !== "stock"), [esoOrders]);
  const stockOrders = useMemo(() => [
    ...orders.filter(o => !o._is_export && o.order_against === "stock"),
    ...esoOrders.filter(o => o.order_against === "stock"),
  ], [orders, esoOrders]);
  const visibleOrders = useMemo(
    () => (sourceKind === "export" ? nonStockEso : sourceKind === "stock" ? stockOrders : domesticOrders),
    [sourceKind, domesticOrders, nonStockEso, stockOrders],
  );
  const sourceLabel = { domestic: "Sales Order", export: "ESO", stock: "Stock Order" }[sourceKind];
  // Per-line-item allocation status for the picked SO. For each article on the SO,
  // sum active-plan meters (Planned/Running) matching this (order_id, item_id) and
  // compute Order → Allocated → Balance so the planner can see how much of each
  // line item still needs machine capacity.
  //
  // Sep 05, 2026 · When the SO is linked to an APPROVED Work Order, the Order
  // column now reflects the WO's `total_qty` (production_qty · ordered + rejection
  // allowance) per line so planners size capacity for the WO cap they must
  // deliver — not just the raw SO qty. Falls back to the SO line's own qty
  // whenever no matching WO line exists (legacy / draft WO / cross-item mix).
  //
  // Sep 05, 2026 · Also deducts the SO line's `cancelled_qty` (and the WO's
  // mirrored `line_cancelled_qty`) so partially-cancelled orders show the
  // TRUE remaining production cap, not the historical top-line qty. When the
  // SO line is `short_closed` (or its WO mirror `line_short_closed`) the cap
  // collapses to 0 so planners can't accidentally allocate against a closed line.
  const soLineItemsAll = useMemo(() => {
    if (!selectedOrder?.items) return [];
    const active = plans.filter(p => ["Planned", "Running"].includes(p.status) && p.order_id === selectedOrderId);
    // Approved WOs pointing at this SO — used to sup the Order column with WO qty.
    const woForOrder = (productionOrders || []).filter(w => (w.source_order_id || "") === selectedOrderId);
    return (selectedOrder.items || []).map(li => {
      const article = li.article_no || "";
      const soLineQty = +(li.order_qty || li.total_qty || li.quantity || li.qty || 0);
      const soCancelled = +(li.cancelled_qty || 0);
      const soShortClosed = !!li.short_closed;
      // Locate the WO line that matches this SO row by product_code / article_no.
      // A single-item WO carries `production_qty` at root; multi-item WOs stash
      // it inside `items[].total_qty`. We prefer the exact line match, then
      // fall back to the root value when only one WO is linked with one line.
      let woQty = 0;
      let woCancelled = 0;
      let woShortClosed = false;
      for (const w of woForOrder) {
        const wItems = Array.isArray(w.items) ? w.items : [];
        const match = wItems.find(x => (x.product_code || x.article_no || "") === article);
        if (match) {
          woQty = +(match.total_qty || match.production_qty || match.order_qty || 0);
          woCancelled = +(match.cancelled_qty || w.line_cancelled_qty || 0);
          woShortClosed = !!(match.short_closed || w.line_short_closed);
          break;
        }
        // Single-item WO fallback: root-level `production_qty` when article
        // matches the WO's item_code (aliased as product_code on the line).
        if (!woQty && (w.item_code === article || wItems.length === 1) && +w.production_qty > 0) {
          woQty = +w.production_qty;
          woCancelled = +(w.line_cancelled_qty || 0);
          woShortClosed = !!w.line_short_closed;
        }
      }
      // Cancelled deduction: prefer the WO's mirrored value (it's the
      // downstream source-of-truth once a WO is approved). Fall back to the
      // SO line's cancelled_qty otherwise.
      const cancelled = woQty > 0 ? (woCancelled || soCancelled) : soCancelled;
      const shortClosed = woQty > 0 ? (woShortClosed || soShortClosed) : soShortClosed;
      const grossOrderQty = woQty > 0 ? woQty : soLineQty;
      const orderQty = shortClosed ? 0 : Math.max(0, grossOrderQty - cancelled);
      const item = items.find(i => i.description === article || i.article_no === article || i.code === article);
      const allocated = active
        .filter(p => item?.id && p.item_id === item.id)
        .reduce((s, p) => s + (+p.planned_qty || 0), 0);
      return {
        article_no: article,
        order_qty: orderQty,
        gross_order_qty: grossOrderQty,
        cancelled_qty: cancelled,
        short_closed: shortClosed,
        so_line_qty: soLineQty,
        wo_qty: woQty,
        allocated_qty: allocated,
        balance_qty: Math.max(0, orderQty - allocated),
        item_id: item?.id || null,
        matched: !!item,
      };
    }).filter(row => row.article_no);
  }, [selectedOrder, plans, selectedOrderId, items, productionOrders]);
  // Feb 2026 · Strict Single-Item Filtering — when a WO is picked, the
  // SO Line Items grid must show ONLY the specific article(s) that
  // particular WO produces (`wo.item_code` or the codes on `wo.items[]`).
  // Sibling SO lines that belong to OTHER WOs are hidden so the planner
  // can't accidentally allocate against a different WO's balance.
  // Split-Head allocations still see every article on the SO via the
  // separate `orderArticles` list below, which is derived from the
  // unfiltered `soLineItemsAll` set.
  const selectedWoForFilter = useMemo(
    () => (selectedPoId ? productionOrders.find(p => p.id === selectedPoId) : null),
    [selectedPoId, productionOrders]
  );
  const soLineItems = useMemo(() => {
    // Feb 2026 · Multi-WO awareness — when the planner has attached
    // additional WOs (Split-Head flow), the grid MUST surface one row
    // per attached WO so operators see every Product Code + Order /
    // Alloc / Balance side-by-side. Rows are built directly from the
    // WO records (source-of-truth) rather than filtering the parent
    // SO's line list — that way additional WOs that live on a
    // different SO still contribute their qty.
    const ids = [selectedPoId, ...Array.from(additionalPoIds)].filter(Boolean);
    if (ids.length === 0) return soLineItemsAll;
    const active = plans.filter(p => ["Planned", "Running"].includes(p.status));
    const rows = [];
    const seen = new Set();
    for (const woId of ids) {
      const wo = productionOrders.find(p => p.id === woId);
      if (!wo) continue;
      // A WO may carry multiple items — emit one row per unique article.
      const woLines = (wo.items && wo.items.length > 0)
        ? wo.items.map(it => ({
            article: (it || {}).item_code || (it || {}).article_no || wo.item_code || "",
            qty: +((it || {}).total_qty || (it || {}).production_qty || (it || {}).order_qty || wo.production_qty || 0),
            cancelled: +((it || {}).cancelled_qty || 0),
            short_closed: !!(it || {}).short_closed,
          }))
        : [{
            article: wo.item_code || "",
            qty: +(wo.production_qty || 0),
            cancelled: +(wo.line_cancelled_qty || 0),
            short_closed: !!wo.line_short_closed,
          }];
      for (const wl of woLines) {
        if (!wl.article) continue;
        const key = `${woId}|${wl.article}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const grossOrderQty = wl.qty;
        const orderQty = wl.short_closed ? 0 : Math.max(0, grossOrderQty - wl.cancelled);
        const item = items.find(i => i.description === wl.article || i.article_no === wl.article || i.code === wl.article);
        const allocated = active
          .filter(p => (p.source_wo_no === wo.wo_no) || (p.work_order_id === wo.id) || (item?.id && p.item_id === item.id && p.order_id === wo.source_order_id))
          .reduce((s, p) => s + (+p.planned_qty || 0), 0);
        rows.push({
          article_no: wl.article,
          order_qty: orderQty,
          gross_order_qty: grossOrderQty,
          cancelled_qty: wl.cancelled,
          short_closed: wl.short_closed,
          so_line_qty: 0,
          wo_qty: grossOrderQty,
          allocated_qty: allocated,
          balance_qty: Math.max(0, orderQty - allocated),
          item_id: item?.id || null,
          matched: !!item,
          wo_no: wo.wo_no || wo.po_no,
          source_order_no: wo.source_order_no || "",
        });
      }
    }
    return rows.length > 0 ? rows : soLineItemsAll;
  }, [selectedPoId, additionalPoIds, productionOrders, plans, items, soLineItemsAll]);
  // Articles offered inside `HeadSplitEditor` — Feb 2026 refactor:
  // ONLY the articles that belong to the currently-attached Work
  // Orders (primary + additional). This means "+ Add" in the split
  // editor is effectively a WO search — planners must attach another
  // WO at the top (Additional WOs · Split-Head picker) before its
  // article becomes selectable here.
  const orderArticles = useMemo(() => Array.from(new Set(soLineItems.map(r => r.article_no).filter(Boolean))), [soLineItems]);
  // Matching Item record from Product Master. Product Master's Article No lives in
  // the `description` field (see Items master schema). Fall back to `article_no` and
  // `code` for legacy rows.
  const matchedItem = useMemo(() => {
    if (!selectedArticle) return null;
    const a = String(selectedArticle).trim();
    return items.find(i => i.description === a || i.article_no === a || i.code === a) || null;
  }, [items, selectedArticle]);

  const picksPerCm = +(matchedItem?.standard_picks_per_cm ?? matchedItem?.picks_per_cm ?? 0);
  const speedRpm = +(matchedItem?.standard_speed_rpm ?? 0);
  const stdEff = +(matchedItem?.standard_efficiency_pct ?? 0);

  // User's formula: `Speed / Picks_per_cm × 60 / 100` → m/hr per head (theoretical)
  const prodPerHrPerHead = useMemo(() => {
    if (!picksPerCm || !speedRpm) return 0;
    return (speedRpm / picksPerCm) * (60 / 100);
  }, [picksPerCm, speedRpm]);
  // Effective m/hr per head after efficiency
  const effProdPerHrPerHead = useMemo(() => prodPerHrPerHead * (stdEff / 100 || 1), [prodPerHrPerHead, stdEff]);
  // Machine daily output = per-head × heads × hours/day
  const derivedProdPerDayPerMachine = useMemo(() => {
    return Math.round(effProdPerHrPerHead * (+heads || 0) * (+hoursPerDay || 0));
  }, [effProdPerHrPerHead, heads, hoursPerDay]);

  // Reset flag — set to true for one render after clicking the Stage 2 Reset button
  // so the "auto-hydrate from selected SO" effect at line 122 and the "auto-push
  // derived daily output" effect below skip a beat instead of clobbering the fresh
  // defaults. Cleared inside a microtask once the reset settles.
  const [resettingCalc, setResettingCalc] = useState(false);

  // Cascade Changeover & Setup times from the Product Master (minutes → hours)
  // as soon as an article is matched. Planners can still override in the input
  // fields below — the effect re-syncs whenever the article changes so a swap
  // between two products picks up the new product's shop-floor defaults.
  useEffect(() => {
    if (resettingCalc) return;
    if (!matchedItem) return;
    const co = +matchedItem.standard_changeover_min;
    const su = +matchedItem.standard_setup_min;
    if (Number.isFinite(co) && co >= 0) setChangeoverHours(+(co / 60).toFixed(2));
    if (Number.isFinite(su) && su >= 0) setSetupHours(+(su / 60).toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedItem?.id, resettingCalc]);

  const resetCalculator = () => {
    setSelectedPoId("");
    setSelectedOrderId("");
    setSelectedArticle("");
    setSelectedMachineTypeId("");
    setSelectedMachineIds(new Set());
    setPerMachineQty({});
    setPerMachineStart({});
    setPerMachineSplits({});
    setOrderQty(200000);
    setDeliveryDays(30);
    setProdPerMachine(2700);
    setAvailableMachines(3);
    setResettingCalc(true);
    // Clear the flag on the next microtask so subsequent user edits work normally.
    queueMicrotask(() => setResettingCalc(false));
    toast.success("Calculator reset to defaults");
  };

  // When a Work Order is picked, resolve its underlying source (SO or
  // ESO), auto-select the article (PO's product code) and hydrate Order Qty
  // with the PO's production_qty (ordered + rejection allowance). This lets
  // every downstream SO-driven memo keep working without changes.
  //
  // Feb 2026 · Source-of-Truth refactor — everything below is now
  // resolved STRICTLY from the WO record. No SO fallback: if the WO
  // lacks item_code / production_qty, we leave the state alone rather
  // than falling through to the linked SO's fields (which caused the
  // WO-18 / SAFETY ORANGE mismatch when the SO carried a different item).
  useEffect(() => {
    if (!selectedPoId) return;
    const po = productionOrders.find(p => p.id === selectedPoId);
    if (!po) return;
    const linked = [...orders, ...esoOrders].find(o => o.id === po.source_order_id);
    if (linked?.order_against === "stock") setSourceKind("stock");
    else if (po.source_type === "ESO") setSourceKind("export");
    else setSourceKind("domestic");
    setSelectedOrderId(po.source_order_id || "");
    // Strict WO → article resolution: root item_code first, then the
    // first non-empty items[].item_code / article_no. No SO fallback.
    let woArticle = po.item_code || "";
    if (!woArticle && Array.isArray(po.items)) {
      for (const it of po.items) {
        const c = (it && (it.item_code || it.article_no)) || "";
        if (c) { woArticle = c; break; }
      }
    }
    if (woArticle) setSelectedArticle(woArticle);
    if (+po.production_qty > 0) setOrderQty(+po.production_qty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPoId, productionOrders]);

  // Feb 2026 · Reverse sync — when the planner picks an SO/ESO directly,
  // auto-select the linked Work Order (if any) so the two pickers stay in
  // step. If the SO/ESO doesn't have an approved WO yet, the WO picker is
  // simply cleared and the planner can still plan against the raw order.
  useEffect(() => {
    if (!selectedOrderId) return;
    // Only auto-flip when the current WO doesn't already point at this SO —
    // prevents an update-loop with the effect above.
    const currentWo = productionOrders.find(p => p.id === selectedPoId);
    if (currentWo && currentWo.source_order_id === selectedOrderId) return;
    const wo = productionOrders.find(p => p.source_order_id === selectedOrderId);
    setSelectedPoId(wo ? wo.id : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderId, productionOrders]);

  // When an SO is picked, hydrate Order Qty from its aggregate order quantity.
  useEffect(() => {
    if (resettingCalc) return;
    if (!selectedOrder) return;
    // Pick the first article by default, else keep current if it still applies.
    if (orderArticles.length > 0 && !orderArticles.includes(selectedArticle)) {
      setSelectedArticle(orderArticles[0]);
    }
    // Order Qty for the picked article — use the *remaining balance* (order qty
    // minus already-allocated meters) so planners always allocate the pending gap
    // instead of the gross line-item quantity.
    const article = selectedArticle || orderArticles[0];
    const row = soLineItems.find(r => r.article_no === article);
    if (row) {
      const nextQty = row.balance_qty > 0 ? row.balance_qty : row.order_qty;
      if (nextQty > 0) setOrderQty(nextQty);
    } else {
      const li = (selectedOrder.items || []).find(x => x.article_no === article);
      const qty = li ? +(li.order_qty || li.total_qty || li.quantity || li.qty || 0) : (selectedOrder.items || []).reduce((s, x) => s + (+x.order_qty || +x.total_qty || +x.quantity || +x.qty || 0), 0);
      if (qty > 0) setOrderQty(qty);
    }
    // Delivery days from SO date → delivery date if both present
    if (selectedOrder.order_date && selectedOrder.delivery_date) {
      const from = new Date(selectedOrder.order_date);
      const to = new Date(selectedOrder.delivery_date);
      const days = Math.max(1, Math.ceil((to - from) / 86400000));
      setDeliveryDays(days);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderId, selectedArticle]);

  // Auto-push derived daily output into the main calculator's yellow field.
  useEffect(() => {
    if (resettingCalc) return;
    if (derivedProdPerDayPerMachine > 0) setProdPerMachine(derivedProdPerDayPerMachine);
  }, [derivedProdPerDayPerMachine, resettingCalc]);

  const num = (v) => (Number.isFinite(+v) && +v > 0 ? +v : 0);
  const reqPerDay = useMemo(() => {
    const d = num(deliveryDays); return d > 0 ? Math.ceil(num(orderQty) / d) : 0;
  }, [orderQty, deliveryDays]);
  const machinesNeeded = useMemo(() => {
    const p = num(prodPerMachine); return p > 0 ? Math.ceil(reqPerDay / p) : 0;
  }, [reqPerDay, prodPerMachine]);
  const allottedCapacity = useMemo(() => num(availableMachines) * num(prodPerMachine), [availableMachines, prodPerMachine]);
  const actualDays = useMemo(() => {
    return allottedCapacity > 0 ? Math.ceil(num(orderQty) / allottedCapacity) : 0;
  }, [orderQty, allottedCapacity]);

  const shortfall = machinesNeeded - num(availableMachines);
  const meetsDue = actualDays > 0 && actualDays <= num(deliveryDays);
  const fmt = (n) => Number.isFinite(+n) ? (+n).toLocaleString("en-IN") : "—";
  const fmtDec = (n) => Number.isFinite(+n) ? (+n).toFixed(2) : "—";

  const Row = ({ label, children, tone }) => (
    <div className={`grid grid-cols-2 gap-3 items-center border-b border-[var(--border)]/60 py-2.5 ${tone || ""}`}>
      <div className="text-xs text-zinc-300">{label}</div>
      <div className="text-right">{children}</div>
    </div>
  );

  // Feb 2026 · Clear resets EVERY sticky selection on the calculator —
  // the primary WO, additional WOs, compat error, article + qty
  // hydration, machine model, machine selections, per-machine
  // start-date / qty / split-head configuration, and the freeze
  // toolbar state — so the planner is dropped back to a truly blank
  // canvas rather than a partial reset.
  const clearSource = () => {
    setSelectedOrderId("");
    setSelectedPoId("");
    setAdditionalPoIds(new Set());
    setAdditionalPoErr("");
    setSelectedArticle("");
    setOrderQty(0);
    setSelectedMachineTypeId("");
    setSelectedMachineIds(new Set());
    setPerMachineStart({});
    setPerMachineQty({});
    setPerMachineSplits({});
    setSourceKind("domestic");
  };

  // ---- Stage 3 · Machine Availability ----------------------------------
  // Machines are "idle-ish" when their runtime status is one of the ready-to-load buckets.
  const IDLE_STATUSES = new Set(["Idle", "Setup", "Power Off"]);
  // Overlap check: for the picked start-date + estimated finish, find plans on each
  // machine that would collide. Only Planned/Running plans block; Completed rows
  // are historical and don't matter here.
  //
  // Sep 05, 2026 · Bug fix — the Freeze Capacity list previously flagged
  // NL-021 (or any loom booked until 2026-09-27) as "Idle" whenever the
  // picked freeze window ended BEFORE that plan started (no overlap →
  // no busy match → row rendered as Idle). Planners were then able to
  // tick the machine and freeze a colliding second bar on top. We now
  // treat ANY active plan whose `planned_finish >= startISO` as
  // occupation — i.e., if the loom is booked out beyond the picked
  // start date, it's busy regardless of whether the current qty happens
  // to fit before the existing bar. Callers still see `planned_finish`
  // on the row so "Queue After" continues to work end-to-end.
  const activePlans = useMemo(() => plans.filter(p => ["Planned", "Running"].includes(p.status)), [plans]);
  const busyPlanFor = (mid, startISO, _finishISO) => {
    if (!startISO) return null;
    const future = activePlans
      .filter(p => p.machine_id === mid && (p.planned_finish || "") >= startISO)
      .sort((a, b) => (a.planned_start || "").localeCompare(b.planned_start || ""));
    return future[0] || null;
  };
  // Estimated finish for the currently picked start date (uses derived daily output).
  const perLoomDays = Math.max(1, Math.ceil((+orderQty || 0) / (selectedMachineIds.size || 1) / (derivedProdPerDayPerMachine || prodPerMachine || 1)));
  const estimatedFinish = useMemo(() => {
    try { return new Date(new Date(startDate).getTime() + (perLoomDays - 1) * 86400000).toISOString().slice(0, 10); }
    catch { return startDate; }
  }, [startDate, perLoomDays]);
  const matchingMachines = useMemo(() => {
    if (!selectedMachineTypeId) return [];
    return machines.filter(m => m.machine_type_id === selectedMachineTypeId);
  }, [machines, selectedMachineTypeId]);
  // Feb 2026 · Per-machine "Proposed Start Date" support.
  //   defaultStartFor(m)  — machine's next available opening: day-after
  //                        the latest active plan finish, else the global
  //                        Freeze-Capacity Start date.
  //   startFor(m)         — effective proposed start: user override
  //                        (`perMachineStart`) wins, else the default.
  //   conflictForMachine  — finds an active Planned/Running plan on this
  //                        loom that would overlap the picked [start, finish]
  //                        window. Powers the inline red warning below the
  //                        date input.
  const defaultStartFor = (m) => {
    if (!m) return startDate;
    const bookings = activePlans
      .filter(p => p.machine_id === m.id)
      .sort((a, b) => (b.planned_finish || "").localeCompare(a.planned_finish || ""));
    const latest = bookings[0]?.planned_finish;
    if (latest) {
      try { return new Date(new Date(latest).getTime() + 86400000).toISOString().slice(0, 10); }
      catch { return startDate; }
    }
    return startDate;
  };
  const startFor = (m) => perMachineStart[m.id] || defaultStartFor(m);
  const conflictForMachine = (m, startISO, finishISO) => {
    if (!m || !startISO) return null;
    // When qty-based finish can't be computed (e.g. article has no
    // Product Master speed specs), fall back to a same-day window so
    // any overlap with an existing plan is still caught.
    const winEnd = finishISO || startISO;
    return activePlans.find(p =>
      p.machine_id === m.id &&
      (p.planned_start || "") <= winEnd &&
      (p.planned_finish || "") >= startISO
    ) || null;
  };
  const todayISO = new Date().toISOString().slice(0, 10);
  // Sep 05, 2026 · Bug fix — "Idle" counters + list must EXCLUDE any
  // machine that already carries a future planned/running bar. Runtime
  // status alone (Idle / Setup / Power Off) was misleading planners
  // into freezing colliding capacity on already-booked looms.
  const isMachineFree = (m) => IDLE_STATUSES.has(m.status) && !busyPlanFor(m.id, startDate, estimatedFinish);
  const idleMatching = useMemo(() => matchingMachines.filter(isMachineFree),
    [matchingMachines, activePlans, startDate, estimatedFinish]);
  const idleAlternates = useMemo(() => {
    if (!selectedMachineType) return [];
    const cat = selectedMachineType.category;
    return machines.filter(m =>
      m.machine_type_id && m.machine_type_id !== selectedMachineTypeId &&
      isMachineFree(m) &&
      (machineTypes.find(t => t.id === m.machine_type_id)?.category === cat)
    );
  }, [machines, selectedMachineType, selectedMachineTypeId, machineTypes, activePlans, startDate, estimatedFinish]);
  // Machines that ARE booked out through/after the picked start date —
  // rendered in a dedicated "Occupied — Queue After" panel below the
  // Idle list so planners can still schedule back-to-back without
  // mistaking the loom for free capacity.
  const occupiedMatching = useMemo(() => matchingMachines.filter(m =>
    IDLE_STATUSES.has(m.status) && busyPlanFor(m.id, startDate, estimatedFinish)
  ), [matchingMachines, activePlans, startDate, estimatedFinish]);
  // Sep 05, 2026 · Testing-agent iter46 · The Freeze Capacity list must
  // SHOW booked looms with the amber 'OCCUPIED · frozen until' chip so
  // planners can still fire the 'Queue After' flow. We merge idle first
  // (selectable) then occupied (disabled) so the top of the list stays
  // action-ready. The existing `busy` / `disabled` render branches
  // handle the styling — no template changes needed.
  const freezeListMatching = useMemo(
    () => [...idleMatching, ...occupiedMatching],
    [idleMatching, occupiedMatching]
  );
  const occupiedAlternates = useMemo(() => {
    if (!selectedMachineType) return [];
    const cat = selectedMachineType.category;
    return machines.filter(m =>
      m.machine_type_id && m.machine_type_id !== selectedMachineTypeId &&
      IDLE_STATUSES.has(m.status) && busyPlanFor(m.id, startDate, estimatedFinish) &&
      (machineTypes.find(t => t.id === m.machine_type_id)?.category === cat)
    );
  }, [machines, selectedMachineType, selectedMachineTypeId, machineTypes, activePlans, startDate, estimatedFinish]);
  const freezeListAlternates = useMemo(
    () => [...idleAlternates, ...occupiedAlternates],
    [idleAlternates, occupiedAlternates]
  );

  // Machines already allotted to the picked SO (Feb 2026). Groups every active
  // (Planned/Running) production-plan on this SO by machine so the planner can
  // see, at a glance, WHICH physical loom is already committed to this SO,
  // WHAT article + qty it's carrying, and OVER WHICH date window. Machines
  // frozen elsewhere (other SOs) aren't listed here; the availableMachines
  // count above already excludes them.
  // Feb 2026 · Plan → WO association helper. A single SO can spawn
  // multiple Work Orders (one per line), so filtering plans by order_id
  // alone lumps every line's loom together. Correct link:
  //   1. plan.line_id === wo.source_line_id  (canonical, one-to-one)
  //   2. plan.line_id ∈ wo.items[].line_id  (multi-line WOs)
  //   3. plan.item_id ∈ wo.item_id ∪ wo.items[].item_id  (fallback when
  //      the plan carries no line_id — item_id is unique per WO line).
  // Returns true when the plan belongs to the given WO.
  const planBelongsToWo = (p, wo) => {
    if (!p || !wo) return false;
    const lineIds = new Set([
      wo.source_line_id,
      ...((wo.items || []).map(it => it?.line_id)),
    ].filter(Boolean));
    if (p.line_id && lineIds.size && lineIds.has(p.line_id)) return true;
    if (p.line_id) return false; // plan has a line_id and it didn't match — reject
    const itemIds = new Set([
      wo.item_id,
      ...((wo.items || []).map(it => it?.item_id)),
    ].filter(Boolean));
    if (p.item_id && itemIds.size && itemIds.has(p.item_id)) return true;
    return false;
  };
  // Reverse lookup — return the WO doc that "owns" a given plan (or
  // null when no WO scopes it). Used by the SO-picker chip so it only
  // surfaces the WO specifically scheduled on this plan, not any WO
  // that happens to share the SO.
  const woForPlan = (p) => {
    if (!p) return null;
    const scoped = productionOrders.filter(w => (w.source_order_id || "") === p.order_id);
    return scoped.find(w => planBelongsToWo(p, w)) || null;
  };
  const allottedMachines = useMemo(() => {
    if (!selectedOrderId) return [];
    const selectedWo = selectedPoId ? productionOrders.find(p => p.id === selectedPoId) : null;
    const active = plans.filter(p =>
      ["Planned", "Running"].includes(p.status) &&
      p.order_id === selectedOrderId &&
      (selectedWo ? planBelongsToWo(p, selectedWo) : true)
    );
    return active.map(p => {
      const m = machines.find(x => x.id === p.machine_id);
      const mt = m ? machineTypes.find(t => t.id === m.machine_type_id) : null;
      const article = items.find(i => i.id === p.item_id);
      return {
        plan_id: p.id,
        machine_id: p.machine_id,
        machine_no: m?.machine_no || m?.name || p.machine_id?.slice(-6) || "—",
        machine_model: mt?.machine_model || mt?.name || "",
        article_no: article?.description || article?.article_no || "—",
        planned_qty: +p.planned_qty || 0,
        status: p.status,
        start_date: p.start_date || p.planned_start || "",
        end_date: p.end_date || p.planned_end || "",
      };
    }).sort((a, b) => (a.machine_no || "").localeCompare(b.machine_no || "", undefined, { numeric: true }));
  }, [plans, machines, machineTypes, items, selectedOrderId, selectedPoId, productionOrders]);

  // Aggregated Machine Model availability (Feb 2026): the moment a Sales Order
  // is picked — BEFORE the planner has narrowed down to a specific Machine
  // Model — surface every model that has at least one loom in the shop-floor
  // pool, along with how many are free right now and the earliest date any
  // loom on that model becomes free. Sorted by free-now count desc, then by
  // earliest-free date. Clicking a row snaps the Machine Model dropdown to
  // that model so the planner can drill in without extra hunting.
  const modelAvailability = useMemo(() => {
    if (!selectedOrderId) return [];
    const todayD = new Date();
    todayD.setHours(0, 0, 0, 0);
    const todayISO = todayD.toISOString().slice(0, 10);
    return machineTypes
      .filter(mt => mt.machine_model)
      .map(mt => {
        const pool = machines.filter(m => m.machine_type_id === mt.id);
        let freeNow = 0;
        let earliestFree = "";
        pool.forEach(m => {
          const active = plans.filter(p =>
            p.machine_id === m.id &&
            ["Planned", "Running"].includes(p.status) &&
            (p.planned_finish || p.end_date || p.planned_end)
          );
          let latestEnd = "";
          active.forEach(p => {
            const end = p.planned_finish || p.end_date || p.planned_end || "";
            if (end && end > latestEnd) latestEnd = end;
          });
          const isFree = !latestEnd || latestEnd < todayISO;
          if (isFree) freeNow++;
          let mFreeFrom = todayISO;
          if (latestEnd) {
            const d = new Date(latestEnd);
            d.setDate(d.getDate() + 1);
            mFreeFrom = d.toISOString().slice(0, 10);
            if (mFreeFrom < todayISO) mFreeFrom = todayISO;
          }
          if (!earliestFree || mFreeFrom < earliestFree) earliestFree = mFreeFrom;
        });
        return {
          id: mt.id,
          machine_model: mt.machine_model,
          name: mt.name,
          category: mt.category,
          no_of_heads: mt.no_of_heads,
          machine_width: mt.machine_width,
          total: pool.length,
          free_now: freeNow,
          earliest_free: earliestFree || todayISO,
        };
      })
      .filter(row => row.total > 0)
      .sort((a, b) => {
        if (b.free_now !== a.free_now) return b.free_now - a.free_now;
        return (a.earliest_free || "").localeCompare(b.earliest_free || "");
      });
  }, [selectedOrderId, machineTypes, machines, plans]);

  // Plans currently frozen for this SO, grouped by machine_id so head-split
  // shards on the same loom collapse into a single row with a "+N shards"
  // chip. Groups where at least one plan matches `matchedItem` are surfaced
  // first (relevance ordering for the article the planner is looking at).
  const frozenForCurrent = useMemo(() => {
    if (!selectedOrderId) return [];
    const plansOnSO = activePlans.filter(p => p.order_id === selectedOrderId);
    if (plansOnSO.length === 0) return [];
    const byMachine = new Map();
    plansOnSO.forEach(p => {
      const arr = byMachine.get(p.machine_id) || [];
      arr.push(p);
      byMachine.set(p.machine_id, arr);
    });
    const groups = Array.from(byMachine.entries()).map(([mid, ps]) => {
      const mach = machines.find(x => x.id === mid);
      const totalQty = ps.reduce((s, p) => s + (+p.planned_qty || 0), 0);
      const totalHeads = ps.reduce((s, p) => s + (+p.heads_used || 0), 0);
      const startDate = ps.map(p => p.planned_start).sort()[0] || "";
      const finishDate = ps.map(p => p.planned_finish).sort().reverse()[0] || "";
      const hasCurrentArticle = !!(matchedItem?.id && ps.some(p => p.item_id === matchedItem.id));
      return { machine_id: mid, machine_code: mach?.code || mid, machine_model: mach?.machine_model || "", plans: ps, total_qty: totalQty, total_heads: totalHeads, start: startDate, finish: finishDate, has_current: hasCurrentArticle };
    });
    // Groups containing the currently-selected Product Code float to the top.
    groups.sort((a, b) => {
      if (a.has_current && !b.has_current) return -1;
      if (!a.has_current && b.has_current) return 1;
      return (a.machine_code || "").localeCompare(b.machine_code || "", undefined, { numeric: true });
    });
    return groups;
  }, [activePlans, selectedOrderId, machines, matchedItem]);

  // Machine availability timeline (Feb 2026): for every loom of the picked
  // machine model, compute the earliest date it becomes free — i.e. one day
  // AFTER the latest end_date of any active plan on it. Sorted earliest-first
  // so the planner sees which loom to grab next. "Free now" = no active plans
  // OR all active plans already ended.
  const machineFreeFrom = useMemo(() => {
    if (!selectedMachineTypeId) return [];
    const pool = machines.filter(m => m.machine_type_id === selectedMachineTypeId);
    const todayD = new Date();
    todayD.setHours(0, 0, 0, 0);
    const today = todayD.toISOString().slice(0, 10);
    // 14-day horizon (Feb 2026 · calendar-strip visualisation) — build the
    // array of ISO dates once so each machine row can just look up occupancy
    // per day without recomputing.
    const HORIZON_DAYS = 14;
    const horizon = Array.from({ length: HORIZON_DAYS }, (_, i) => {
      const d = new Date(todayD); d.setDate(d.getDate() + i);
      return d.toISOString().slice(0, 10);
    });
    return pool.map(m => {
      const active = plans.filter(p =>
        p.machine_id === m.id &&
        ["Planned", "Running"].includes(p.status) &&
        (p.planned_finish || p.end_date || p.planned_end)
      );
      let latestEnd = "";
      let latestPlan = null;
      active.forEach(p => {
        const end = p.planned_finish || p.end_date || p.planned_end || "";
        if (end && end > latestEnd) { latestEnd = end; latestPlan = p; }
      });
      let freeFrom = today;
      if (latestEnd) {
        const d = new Date(latestEnd);
        d.setDate(d.getDate() + 1);
        freeFrom = d.toISOString().slice(0, 10);
        if (freeFrom < today) freeFrom = today;
      }
      const isFreeNow = !latestEnd || latestEnd < today;
      const busySo = latestPlan ? (orders.find(o => o.id === latestPlan.order_id)?.order_no || "") : "";
      // Per-day occupancy for the 14-day strip: a day is BUSY if any active
      // plan on this machine covers it (start_date <= day <= end_date).
      const strip = horizon.map(day => {
        const occupier = active.find(p => {
          const s = p.planned_start || p.start_date || "";
          const e = p.planned_finish || p.end_date || p.planned_end || "";
          return s && e && day >= s && day <= e;
        });
        return occupier
          ? { day, busy: true, so_id: occupier.order_id }
          : { day, busy: false };
      });
      return {
        machine_id: m.id,
        machine_no: m.machine_no || m.name || m.id.slice(-6),
        latest_end: latestEnd || "",
        free_from: freeFrom,
        is_free_now: isFreeNow,
        busy_until_so: busySo,
        strip,
      };
    }).sort((a, b) => {
      if (a.is_free_now && !b.is_free_now) return -1;
      if (!a.is_free_now && b.is_free_now) return 1;
      return (a.free_from || "").localeCompare(b.free_from || "");
    });
  }, [machines, plans, orders, selectedMachineTypeId]);

  const unfreezePlan = async (planId) => {
    if (!confirm("Unfreeze this plan? The machine will become available again for this window.")) return;
    try {
      await api.delete(`/production-plans/${planId}`);
      toast.success("Plan unfrozen — machine released");
      reloadPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unfreeze failed");
    }
  };

  // Partial release (Feb 2026): shrink an existing plan by a specified qty so
  // the freed meters return to SO balance and can be re-allocated on another
  // loom to speed up delivery. `releaseInputs` holds the in-progress meter
  // count per plan id. `addInputs` mirrors it for the opposite direction —
  // growing an existing plan by a given qty (extends `planned_finish`).
  const [releaseInputs, setReleaseInputs] = useState({});
  const [addInputs, setAddInputs] = useState({});
  const releasePlanQty = async (planId, currentQty) => {
    const raw = releaseInputs[planId];
    const qty = +raw;
    if (!Number.isFinite(qty) || qty <= 0) { toast.error("Enter a valid quantity to release"); return; }
    if (qty >= currentQty) { toast.error("Use Unfreeze to release the whole plan"); return; }
    try {
      await api.put(`/production-plans/${planId}/release-qty`, { release_qty: qty });
      toast.success(`Released ${fmt(qty)} m from this plan · re-allocate to another loom`);
      setReleaseInputs(r => { const n = { ...r }; delete n[planId]; return n; });
      reloadPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Partial release failed");
    }
  };
  const addPlanQty = async (planId) => {
    const raw = addInputs[planId];
    const qty = +raw;
    if (!Number.isFinite(qty) || qty <= 0) { toast.error("Enter a valid quantity to add"); return; }
    try {
      await api.put(`/production-plans/${planId}/add-qty`, { add_qty: qty });
      toast.success(`Added ${fmt(qty)} m to this plan · finish extended`);
      setAddInputs(r => { const n = { ...r }; delete n[planId]; return n; });
      reloadPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };

  const toggleMachine = (id) => {
    setSelectedMachineIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setPerMachineQty(pq => { const n = { ...pq }; delete n[id]; return n; });
        setPerMachineStart(ps => { const n = { ...ps }; delete n[id]; return n; });
        setPerMachineSplits(sp => { const n = { ...sp }; delete n[id]; return n; });
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // ── Head-Split helpers (Feb 2026) ────────────────────────────────
  // Runs ONE machine's heads across multiple article/colour variants of the
  // same base article in parallel (e.g. an 8-head jacquard running 2h of
  // variant-01 + 4h of variant-02 + 2h of variant-03 simultaneously). Freeze
  // creates one production plan per variant, each carrying its own item_id,
  // planned_qty and heads_used.
  const headsForMachine = (m) => {
    const mt = machineTypes.find(t => t.id === m?.machine_type_id);
    return +(mt?.no_of_heads || 0);
  };
  // ── Per-machine capacity (Feb 2026) ────────────────────────────────
  // Different loom models have different head counts, so the same product
  // runs at different daily outputs on each machine. `machineDailyOutput`
  // scales the shared per-head rate by THIS machine's own heads. The freeze
  // flow and the per-row `≈ Nd` preview both key off it so an 8-head loom's
  // Gantt bar and its 10-head neighbour's Gantt bar land on different finish
  // dates for the same allocated meters.
  const machineDailyOutput = (m) => {
    const h = headsForMachine(m);
    if (!(effProdPerHrPerHead > 0 && h > 0 && hoursPerDay > 0)) return 0;
    return effProdPerHrPerHead * h * hoursPerDay;
  };
  const machineDaysForQty = (m, qty) => {
    const q = +qty || 0;
    if (q <= 0) return 0;
    const h = headsForMachine(m);
    const perHourAtHeads = (effProdPerHrPerHead || 0) * (h || 0);
    if (perHourAtHeads > 0) {
      const productionHours = q / perHourAtHeads;
      const totalHours = (+changeoverHours || 0) + (+setupHours || 0) + productionHours;
      return Math.max(1, Math.ceil(totalHours / (+hoursPerDay || 22)));
    }
    // Feb 2026 · Fallback when the picked Article has no Product-Master
    // speed specs — planners still get an ETA + conflict window by using
    // the manual "Production per Day per Machine" override.
    const perDay = +derivedProdPerDayPerMachine || +prodPerMachine || 0;
    if (perDay > 0) return Math.max(1, Math.ceil(q / perDay));
    return 0;
  };
  const machineFinishFor = (m, mStart, qty) => {
    const days = machineDaysForQty(m, qty);
    if (!days || !mStart) return "";
    try { return new Date(new Date(mStart).getTime() + (days - 1) * 86400000).toISOString().slice(0, 10); }
    catch { return ""; }
  };
  const enableHeadSplit = (m) => {
    const totalHeads = headsForMachine(m);
    setPerMachineSplits(sp => ({
      ...sp,
      [m.id]: [
        { item_id: matchedItem?.id || null, article_no: selectedArticle || "", heads: totalHeads, qty: 0 },
      ],
    }));
    setSelectedMachineIds(prev => {
      const next = new Set(prev);
      next.add(m.id);
      return next;
    });
  };
  const disableHeadSplit = (mid) => {
    setPerMachineSplits(sp => { const n = { ...sp }; delete n[mid]; return n; });
  };
  const addSplitVariant = (mid) => {
    setPerMachineSplits(sp => {
      const rows = sp[mid] || [];
      const used = new Set(rows.map(r => r.article_no));
      const nextArticle = orderArticles.find(a => !used.has(a)) || orderArticles[0] || "";
      const nextItem = items.find(i => i.description === nextArticle || i.article_no === nextArticle || i.code === nextArticle);
      return {
        ...sp,
        [mid]: [...rows, { item_id: nextItem?.id || null, article_no: nextArticle, heads: 1, qty: 0 }],
      };
    });
  };
  const removeSplitVariant = (mid, idx) => {
    setPerMachineSplits(sp => {
      const rows = (sp[mid] || []).filter((_, i) => i !== idx);
      const next = { ...sp };
      if (rows.length === 0) delete next[mid];
      else next[mid] = rows;
      return next;
    });
  };
  const updateSplit = (mid, idx, patch) => {
    setPerMachineSplits(sp => {
      const rows = [...(sp[mid] || [])];
      const row = { ...rows[idx], ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, "article_no")) {
        const it = items.find(i => i.description === patch.article_no || i.article_no === patch.article_no || i.code === patch.article_no);
        row.item_id = it?.id || null;
      }
      rows[idx] = row;
      return { ...sp, [mid]: rows };
    });
  };
  const splitHeadsSum = (mid) => (perMachineSplits[mid] || []).reduce((s, r) => s + (+r.heads || 0), 0);
  const splitQtySum = (mid) => (perMachineSplits[mid] || []).reduce((s, r) => s + (+r.qty || 0), 0);
  const hasSplit = (mid) => !!(perMachineSplits[mid] && perMachineSplits[mid].length > 0);

  // Queue a machine back-to-back after its current occupier. Sets its per-machine
  // start to the day AFTER the blocking plan finishes and adds it to the
  // selected set so the freeze button includes it. Called from the "Queue after"
  // button that only renders on Occupied rows.
  const queueMachineAfter = (mid, busyFinishISO) => {
    if (!busyFinishISO) return;
    const d = new Date(busyFinishISO);
    d.setDate(d.getDate() + 1);
    const qStart = d.toISOString().slice(0, 10);
    setPerMachineStart(ps => ({ ...ps, [mid]: qStart }));
    setSelectedMachineIds(prev => {
      const next = new Set(prev);
      next.add(mid);
      return next;
    });
    toast.success(`Queued back-to-back · starts ${qStart}`);
  };
  const cancelQueue = (mid) => {
    setPerMachineStart(ps => { const n = { ...ps }; delete n[mid]; return n; });
    setSelectedMachineIds(prev => {
      const next = new Set(prev);
      next.delete(mid);
      return next;
    });
    setPerMachineQty(pq => { const n = { ...pq }; delete n[mid]; return n; });
  };

  const selectedItem = useMemo(() => matchedItem, [matchedItem]);
  const selectedIds = Array.from(selectedMachineIds);
  // Even-split default (used when the user hasn't overridden a per-machine qty).
  const evenSplitQty = selectedIds.length > 0 ? Math.ceil((+orderQty || 0) / selectedIds.length) : 0;
  const qtyFor = (id) => {
    if (hasSplit(id)) return splitQtySum(id);
    const v = perMachineQty[id];
    return Number.isFinite(+v) && +v > 0 ? +v : evenSplitQty;
  };
  const totalAllocated = selectedIds.reduce((sum, id) => sum + qtyFor(id), 0);
  const allocationDelta = totalAllocated - (+orderQty || 0);

  // Effective cap for the picked article: what's still un-allocated on the SO line item.
  // Placed AFTER `totalAllocated` so `overCap` can read it without a TDZ error.
  const currentBalance = useMemo(() => {
    const row = soLineItems.find(r => r.article_no === selectedArticle);
    return row ? row.balance_qty : null; // null = no line-item context (fall back to Order Qty input)
  }, [soLineItems, selectedArticle]);
  // Total SO balance across ALL line items — used as the cap the moment any
  // machine enables Head-Split, because a split may allocate to variants
  // OTHER than the currently-picked Product Code. Per-Product-Code balance
  // would falsely block those valid multi-variant allocations.
  const orderBalanceTotal = useMemo(
    () => soLineItems.reduce((s, r) => s + (+r.balance_qty || 0), 0),
    [soLineItems]
  );
  const anySplit = selectedIds.some(mid => hasSplit(mid));
  const capSource = anySplit
    ? "SO Total Balance"
    : (currentBalance !== null ? "Product Balance" : "Order Qty");
  const effectiveCap = anySplit
    ? orderBalanceTotal
    : (currentBalance !== null ? currentBalance : (+orderQty || 0));
  const overCap = Math.max(0, totalAllocated - effectiveCap);

  const setQtyFor = (id, val) => {
    setPerMachineQty(pq => ({ ...pq, [id]: val }));
  };
  const resetAllocations = () => setPerMachineQty({});

  const freezeCapacity = async () => {
    if (!selectedOrderId) { toast.error("Pick a Sales Order first"); return; }
    if (!selectedItem?.id) { toast.error("Article not in Product Master — cannot freeze"); return; }
    if (selectedIds.length === 0) { toast.error("Select at least one machine"); return; }
    if (overCap > 0) {
      toast.error(`Cannot allocate more than the ${capSource} (${fmt(effectiveCap)} m) — reduce by ${fmt(overCap)} m`);
      return;
    }
    const start = startDate;
    // Head-split pre-flight: sum of split heads must equal the machine's total
    // heads, and sum of split qty must be > 0. Otherwise the parallel run
    // wouldn't utilise the loom correctly.
    for (const mid of selectedIds) {
      if (!hasSplit(mid)) continue;
      const mach = machines.find(x => x.id === mid);
      const totalHeads = headsForMachine(mach);
      const sumHeads = splitHeadsSum(mid);
      const sumQty = splitQtySum(mid);
      if (totalHeads > 0 && sumHeads !== totalHeads) {
        toast.error(`Loom ${mach?.code || mid}: split uses ${sumHeads}/${totalHeads} heads — must match total heads`);
        return;
      }
      if (sumQty <= 0) {
        toast.error(`Loom ${mach?.code || mid}: split needs at least one variant with qty > 0`);
        return;
      }
      const bad = (perMachineSplits[mid] || []).find(r => !r.item_id);
      if (bad) {
        toast.error(`Loom ${mach?.code || mid}: variant "${bad.article_no || "—"}" is not in Product Master`);
        return;
      }
    }
    setFreezing(true);
    try {
      const posts = [];
      for (const mid of selectedIds) {
        const mStart = perMachineStart[mid] || start;
        if (hasSplit(mid)) {
          // Multi-variant on ONE loom: parallel runs share the loom's window.
          // Each variant's production hours = qty ÷ (per-head-rate × its heads).
          // Machine finish = max(production_hours across variants) + setup +
          // changeover, ceiled to whole days.
          const splits = perMachineSplits[mid] || [];
          const variantHours = splits.map(r => {
            const perHourAtHeads = (effProdPerHrPerHead || 0) * (+r.heads || 0);
            return perHourAtHeads > 0 ? (+r.qty || 0) / perHourAtHeads : 0;
          });
          const longest = variantHours.length > 0 ? Math.max(...variantHours) : 0;
          const changeover_hours = +(+changeoverHours || 0).toFixed(2);
          const setup_hours = +(+setupHours || 0).toFixed(2);
          const totalHours = changeover_hours + setup_hours + longest;
          const days = Math.max(1, Math.ceil(totalHours / (+hoursPerDay || 22)));
          const finish = new Date(new Date(mStart).getTime() + (days - 1) * 86400000).toISOString().slice(0, 10);
          splits.forEach((r, i) => {
            const production_hours = +(variantHours[i] || 0).toFixed(2);
            posts.push(api.post("/production-plans/", {
              order_id: selectedOrderId,
              item_id: r.item_id,
              // Feb 2026 · Stamp the variant's own product_code on the
              // POST body so `_enrich_plan_dna` never has to guess the
              // WO / product for split-head shards. Fixes the case where
              // two SO lines share an item_id (legacy Explode-BOM data
              // anomaly) and both shards ended up labelled with the
              // first WO's number.
              product_code: r.article_no || "",
              article_no: r.article_no || "",
              machine_id: mid,
              planned_qty: +r.qty || 0,
              planned_start: mStart,
              planned_finish: finish,
              daily_target: derivedProdPerDayPerMachine || prodPerMachine || 0,
              changeover_hours,
              setup_hours,
              production_hours,
              heads_used: +r.heads || 0,
              status: "Planned",
            }));
          });
        } else {
          const qty = qtyFor(mid);
          const mach = machines.find(x => x.id === mid);
          // Per-machine capacity — an 8-head and a 10-head loom have different
          // daily outputs for the same product, so their finish dates diverge
          // on the Gantt board. Read THIS machine's own head count instead of
          // the shared `heads` state (which cascades from the picked model).
          const machHeads = headsForMachine(mach) || +heads || 0;
          const perHourAtHeads = (effProdPerHrPerHead || 0) * machHeads;
          const production_hours = perHourAtHeads > 0 ? +(qty / perHourAtHeads).toFixed(2) : 0;
          const changeover_hours = +(+changeoverHours || 0).toFixed(2);
          const setup_hours = +(+setupHours || 0).toFixed(2);
          const totalHours = changeover_hours + setup_hours + production_hours;
          const days = Math.max(1, Math.ceil(totalHours / (+hoursPerDay || 22)));
          const finish = new Date(new Date(mStart).getTime() + (days - 1) * 86400000).toISOString().slice(0, 10);
          const machDaily = perHourAtHeads * (+hoursPerDay || 22);
          posts.push(api.post("/production-plans/", {
            order_id: selectedOrderId,
            item_id: selectedItem.id,
            machine_id: mid,
            planned_qty: qty,
            planned_start: mStart,
            planned_finish: finish,
            daily_target: Math.round(machDaily) || derivedProdPerDayPerMachine || prodPerMachine || 0,
            changeover_hours,
            setup_hours,
            production_hours,
            heads_used: 0,
            status: "Planned",
          }));
        }
      }
      await Promise.all(posts);
      toast.success(`Froze capacity on ${selectedIds.length} machine${selectedIds.length === 1 ? "" : "s"} · ${fmt(totalAllocated)} m total`);
      setSelectedMachineIds(new Set());
      setPerMachineQty({});
      setPerMachineStart({});
      setPerMachineSplits({});
      reloadPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Freeze failed");
    }
    setFreezing(false);
  };

  return (
    <div className="space-y-4">
      {/* Legend note: Machines "OCCUPIED" (orange) = already frozen on another plan window; not a breakdown. */}
      {/* Stage 1 · Source (Sales Order → Product Master) */}
      <div className="tactical-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline mb-0.5">Source · Work Order + Product Master</div>
            <div className="text-lg font-display font-black text-white">Pull Article Specs → Compute Prod / Hour / Head</div>
            <div className="text-xs text-zinc-500 mt-0.5">Pick a Sales Order + Article. Picks/cm, Speed and Efficiency stream in from the Product Master. Formula: <span className="font-mono">Speed ÷ Picks-per-cm × 60 ÷ 100</span>.</div>
          </div>
          {selectedOrderId && (
            <button data-testid="planner-clear-source" onClick={clearSource} className="h-8 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs rounded-sm text-zinc-400">Clear</button>
          )}
        </div>

        {/* Feb 2026 · WO-first Traceability Strip — SO/ESO No + Customer
            surface as READ-ONLY labels at the top the moment a Work
            Order is picked. Never a basis for grid logic — the grid
            is driven strictly off the WO record. */}
        {selectedPoId && (() => {
          const wo = productionOrders.find(p => p.id === selectedPoId);
          if (!wo) return null;
          const linkedSo = [...orders, ...esoOrders].find(o => o.id === wo.source_order_id);
          const extras = Array.from(additionalPoIds).map(id => productionOrders.find(p => p.id === id)).filter(Boolean);
          return (
            <div data-testid="planner-trace-strip" className="mb-3 rounded-sm border border-emerald-500/25 bg-emerald-500/5 px-3 py-2 flex items-center flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono">
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] uppercase tracking-widest text-zinc-500">Work Order</span>
                <span className="text-emerald-300 font-semibold">{wo.po_no}</span>
              </div>
              {linkedSo && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] uppercase tracking-widest text-zinc-500">{wo.source_type || "SO"}</span>
                  <span className="text-blue-300">{linkedSo.order_no || linkedSo.doc_no}</span>
                </div>
              )}
              {/* Customer hidden on production floor · Mar 2026 */}
              {false && wo.customer_name && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] uppercase tracking-widest text-zinc-500">Customer</span>
                  <span className="text-zinc-300">{wo.customer_name}</span>
                </div>
              )}
              {extras.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] uppercase tracking-widest text-zinc-500">Split-Head WOs</span>
                  {extras.map(x => (
                    <span key={x.id} className="text-purple-300">{x.po_no}</span>
                  ))}
                </div>
              )}
              <span className="ml-auto text-[9px] text-zinc-500 uppercase tracking-widest">Read-only reference</span>
            </div>
          );
        })()}

          {/* Feb 2026 · Single-picker layout (SO/ESO picker removed per
              user request). Work Order is the ONLY entry point; the
              linked SO/ESO surfaces read-only in the "Sourced from"
              chip directly under the picker. */}
        <div className="mb-4">
          {/* --- Work Order (Primary) picker --- */}
          <div>
            <div className="overline mb-1.5 flex items-center justify-between">
              <span>Work Order · Primary</span>
              <span className="text-[9px] normal-case tracking-normal text-emerald-300 font-mono" title="Feb 2026 · WO is the absolute source of truth — Product Code / Qty / Customer stream in strictly from the WO record; the linked SO/ESO is view-only.">
                Source of Truth
              </span>
            </div>
            {(() => {
              const scopedWos = selectedOrderId
                ? productionOrders.filter(p => (p.source_order_id || "") === selectedOrderId)
                : productionOrders;
              const placeholder = productionOrders.length
                ? "Pick a Work Order…"
                : "No approved WOs — approve one in Work Orders";
              return (
                <>
                  <Select value={selectedPoId} onValueChange={setSelectedPoId}>
                    <SelectTrigger data-testid="planner-po-select" className={selCls}>
                      <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-72">
                      {productionOrders.length === 0 && (
                        <div className="px-3 py-4 text-xs text-zinc-500 italic">
                          No approved Work Orders yet — approve one in the Work Orders module first.
                        </div>
                      )}
                      {(selectedOrderId ? scopedWos : productionOrders).map(po => {
                        const linkedSo = [...orders, ...esoOrders].find(o => o.id === po.source_order_id);
                        return (
                          <SelectItem key={po.id} value={po.id}>
                            <span className="mr-1.5 px-1 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 text-[9px] font-mono uppercase tracking-widest">{po.source_type}</span>
                            <span className="font-mono">{po.po_no}</span>
                            {linkedSo && <span className="ml-2 text-zinc-500 text-[10px]">· {linkedSo.order_no || linkedSo.doc_no}</span>}
                            {po.customer_name && <span className="ml-2 text-emerald-300/80 text-[10px] font-mono truncate max-w-[220px]" title={po.item_code || (po.items?.[0]?.item_code) || po.items?.[0]?.article_no || po.customer_name}>· {po.item_code || (po.items?.[0]?.item_code) || po.items?.[0]?.article_no || po.customer_name}</span>}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {selectedPoId && (() => {
                    const wo = productionOrders.find(p => p.id === selectedPoId);
                    if (!wo) return null;
                    const linkedSo = [...orders, ...esoOrders].find(o => o.id === wo.source_order_id);
                    return (
                      <div data-testid="planner-wo-primary-chip" className="mt-1 text-[10px] font-mono flex items-center gap-2 flex-wrap">
                        <span className="text-zinc-500">Sourced from:</span>
                        {linkedSo ? (
                          <span className="px-1 py-0.5 rounded-sm bg-blue-500/15 text-blue-300 text-[9px] uppercase tracking-widest">{wo.source_type || "SO"}</span>
                        ) : null}
                        {linkedSo ? (
                          <span className="text-blue-300">{linkedSo.order_no || linkedSo.doc_no}</span>
                        ) : (
                          <span className="text-amber-400">No linked SO/ESO</span>
                        )}
                        {(() => {
                          const pc = wo.item_code || (wo.items?.[0]?.item_code) || wo.items?.[0]?.article_no;
                          return pc ? (
                            <>
                              <span className="text-zinc-600">·</span>
                              <span className="text-emerald-300 font-mono truncate max-w-[260px]" title={pc}>{pc}</span>
                            </>
                          ) : null;
                        })()}
                        {/* Customer hidden on production floor · Mar 2026 */}
                        {false && wo.customer_name ? (
                          <>
                            <span className="text-zinc-600">·</span>
                            <span className="text-zinc-400">{wo.customer_name}</span>
                          </>
                        ) : null}
                        {+wo.production_qty > 0 ? (
                          <>
                            <span className="text-zinc-600">·</span>
                            <span className="text-emerald-300">{Number(wo.production_qty).toLocaleString("en-IN")} m</span>
                          </>
                        ) : null}
                      </div>
                    );
                  })()}
                  {selectedPoId && (() => {
                    const wo = productionOrders.find(p => p.id === selectedPoId);
                    if (!wo) return null;
                    const primaryKey = widthArticleKeyOf(productCodeOfWo(wo));
                    const eligible = productionOrders.filter(p =>
                      p.id !== selectedPoId && !additionalPoIds.has(p.id)
                    );
                    const tryAdd = (poId) => {
                      const cand = productionOrders.find(p => p.id === poId);
                      if (!cand) return;
                      const candKey = widthArticleKeyOf(productCodeOfWo(cand));
                      if (!primaryKey || !candKey || primaryKey !== candKey) {
                        setAdditionalPoErr(`Cannot add ${cand.po_no}: Width/Article ${candKey || "—"} does not match primary WO's ${primaryKey || "—"}. Split-head requires both WOs to share the same fabric width + base article.`);
                        toast.error(`Cannot add ${cand.po_no} — Width/Article mismatch (${candKey || "—"} vs ${primaryKey || "—"})`);
                        return;
                      }
                      setAdditionalPoIds(prev => new Set([...prev, poId]));
                      setAdditionalPoErr("");
                      toast.success(`Added ${cand.po_no} for split-head allocation`);
                    };
                    return (
                      <div className="mt-2 space-y-1.5" data-testid="planner-multi-wo-block">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Additional WOs · Split-Head</span>
                          <span className="text-[9px] text-emerald-300 font-mono">key {primaryKey || "—"}</span>
                          {Array.from(additionalPoIds).map(id => {
                            const extra = productionOrders.find(p => p.id === id);
                            if (!extra) return null;
                            return (
                              <span key={id} data-testid={`planner-multi-wo-chip-${extra.po_no}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-purple-500/15 border border-purple-500/40 text-purple-200 text-[10px] font-mono">
                                {extra.po_no}
                                <button
                                  type="button"
                                  data-testid={`planner-multi-wo-remove-${extra.po_no}`}
                                  onClick={() => {
                                    setAdditionalPoIds(prev => {
                                      const next = new Set(prev);
                                      next.delete(id);
                                      return next;
                                    });
                                    setAdditionalPoErr("");
                                  }}
                                  className="hover:text-red-300 leading-none"
                                >×</button>
                              </span>
                            );
                          })}
                          <Select value="" onValueChange={tryAdd}>
                            <SelectTrigger data-testid="planner-multi-wo-add" className="h-6 min-w-[190px] w-auto px-2 text-[10px] bg-[var(--bg-0)] border-purple-500/40 text-purple-300 hover:bg-purple-500/10">
                              <SelectValue placeholder="+ Add WO for split-head…" />
                            </SelectTrigger>
                            <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-72">
                              {eligible.length === 0 && (
                                <div className="px-3 py-4 text-xs text-zinc-500 italic">No other WOs available.</div>
                              )}
                              {eligible.map(p => {
                                const code = productCodeOfWo(p);
                                const k = widthArticleKeyOf(code);
                                const match = k && k === primaryKey;
                                return (
                                  <SelectItem key={p.id} value={p.id}>
                                    <span className="mr-1.5 px-1 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 text-[9px] font-mono uppercase tracking-widest">{p.source_type}</span>
                                    <span className="font-mono">{p.po_no}</span>
                                    {code && <span className="ml-2 text-emerald-300/80 text-[10px] font-mono truncate max-w-[220px]" title={code}>· {code}</span>}
                                    <span className={`ml-2 text-[9px] font-mono ${match ? "text-emerald-400" : "text-red-400"}`}>{k || "—"} {match ? "✓" : "✗"}</span>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                        {additionalPoErr && (
                          <div data-testid="planner-multi-wo-err" className="text-[10px] text-red-300 bg-red-500/10 border border-red-500/40 rounded-sm px-2 py-1 flex items-start gap-1.5">
                            <WarningCircleIcon size={11} weight="fill" className="text-red-400 flex-shrink-0 mt-0.5" />
                            <span>{additionalPoErr}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              );
            })()}
          </div>

          {/* --- Feb 2026 · SO/ESO/STOCK picker removed per user
              request. Linked SO/ESO now surfaces read-only through the
              "Sourced from" chip under the WO picker (see above). --- */}
        </div>
        {/* Legacy two-picker layout removed — replaced by the block above. */}

        {/* Feb 2026 · Capacity Calculator 50/50 Split (user request).
            LEFT column carries the SO/WO context (Article grid,
            Allotted Machines) + the Production Requirements math
            (Picks/Speed/Heads/Prod cards).
            RIGHT column carries the loom-availability funnel —
            Free Machine Models table, the selected Machine Model
            summary, and the full Machine Availability list with the
            14-day preview strip.
            Uses the xl breakpoint (1280px+) so laptop-sized screens
            still stack vertically as requested. */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
          <div className="space-y-2.5">
            {selectedArticle && !matchedItem && (
              <div className="text-[11px] text-amber-400 flex items-center gap-1.5"><WarningCircleIcon size={12} weight="bold" />Article not found in Product Master — add it there to pull specs.</div>
            )}

            {/* Order Qty vs Allocated vs Balance — per line item on the picked SO.
                This table IS the article picker (Feb 2026): the Product Code
                dropdown was removed; click any row to select that line item. */}
            {selectedOrderId && soLineItems.length > 0 && (
              <div className="tactical-card p-2.5 border border-[var(--border)]/60" data-testid="planner-alloc-table">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center justify-between">
                  <span>Work Order Item · Pick a Product Code to allocate</span>
                  <div className="flex items-center gap-2">
                    {selectedWoForFilter && soLineItems.length < soLineItemsAll.length && (
                      <span data-testid="planner-wo-filter-chip" className="text-[9px] normal-case tracking-normal text-emerald-300 font-mono px-1.5 py-0.5 rounded-sm bg-emerald-500/10 border border-emerald-500/30" title={`Grid strictly scoped to ${selectedWoForFilter.po_no}'s article. Sibling SO lines from other WOs are hidden. Split-head still lets you add other articles.`}>
                        Filtered to {selectedWoForFilter.po_no}
                      </span>
                    )}
                    {soLineItems.some(r => r.wo_qty > 0) && (
                      <span className="text-[9px] normal-case tracking-normal text-cyan-300 font-mono" title="Order column shows the linked Work Order's production_qty (ordered + rejection allowance). Falls back to SO line qty when no WO is linked.">
                        Order = WO Qty
                      </span>
                    )}
                    {selectedArticle && <span className="text-[9px] text-[var(--accent)] normal-case tracking-normal font-mono truncate max-w-[180px]" title={selectedArticle}>Selected: {selectedArticle}</span>}
                    {/* Feb 2026 · Universal Over-Allocation Guard —
                        instant refresh of the Order/Alloc/Balance grid so
                        planners can clear stale double-counts without a
                        full page reload. */}
                    <button
                      type="button"
                      data-testid="planner-recalc-balance"
                      onClick={recalculateBalance}
                      disabled={recalculating}
                      title="Refresh Order / Alloc / Balance for the picked WO(s) from the server (clears stale double-allocations)"
                      className="text-[9px] normal-case tracking-normal font-mono px-1.5 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <ArrowsClockwiseIcon size={10} weight="bold" />
                      {recalculating ? "Recalculating…" : "Recalculate Balance"}
                    </button>
                  </div>
                </div>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-zinc-500 uppercase tracking-widest text-[9px] border-b border-[var(--border)]/60">
                      <th className="text-left py-1 font-medium">Product Code</th>
                      <th className="text-right py-1 font-medium">Order</th>
                      <th className="text-right py-1 font-medium">Alloc</th>
                      <th className="text-right py-1 font-medium">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soLineItems.map((row, i) => (
                      <tr
                        key={row.article_no + "-" + i}
                        data-testid={`planner-alloc-row-${row.article_no}`}
                        onClick={() => setSelectedArticle(row.article_no)}
                        className={`cursor-pointer border-b border-[var(--border)]/40 last:border-0 ${selectedArticle === row.article_no ? "bg-[var(--accent)]/15 ring-1 ring-[var(--accent)]/40" : "hover:bg-[var(--bg-2)]/40"}`}
                      >
                        <td className="py-1 font-mono text-white truncate max-w-[220px]" title={row.article_no}>
                          {selectedArticle === row.article_no && <span className="text-[var(--accent)] mr-1">▸</span>}
                          {row.article_no}
                        </td>
                        <td className="py-1 text-right font-mono text-zinc-300" title={
                          row.short_closed ? `Line short-closed · cap = 0`
                          : row.cancelled_qty > 0
                            ? `Gross ${fmt(row.gross_order_qty)} − Cancelled ${fmt(row.cancelled_qty)} = ${fmt(row.order_qty)}${row.wo_qty > 0 ? " (WO)" : ""}`
                            : (row.wo_qty > 0 ? `WO qty ${fmt(row.wo_qty)} · SO qty ${fmt(row.so_line_qty)}` : `SO qty ${fmt(row.so_line_qty)}`)
                        }>
                          {fmt(row.order_qty)}
                          {row.wo_qty > 0 && row.wo_qty !== row.so_line_qty && (
                            <span className="ml-1 text-[9px] text-cyan-300/80" title={`Includes ${fmt(row.wo_qty - row.so_line_qty)}m rejection allowance`}>WO</span>
                          )}
                          {row.short_closed && (
                            <span className="ml-1 text-[9px] text-red-400 uppercase tracking-widest">SC</span>
                          )}
                          {row.cancelled_qty > 0 && !row.short_closed && (
                            <span className="ml-1 text-[9px] text-amber-300" title={`${fmt(row.cancelled_qty)}m cancelled — deducted from Order`}>−{fmt(row.cancelled_qty)}</span>
                          )}
                        </td>
                        <td className={`py-1 text-right font-mono ${row.allocated_qty > 0 ? "text-amber-300" : "text-zinc-500"}`}>{fmt(row.allocated_qty)}</td>
                        <td className={`py-1 text-right font-mono font-semibold ${row.balance_qty === 0 ? "text-emerald-400" : "text-white"}`}>{fmt(row.balance_qty)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-[var(--border)]/60 text-[10px] text-zinc-400">
                      <td className="py-1 font-semibold uppercase tracking-widest">Total</td>
                      <td className="py-1 text-right font-mono">{fmt(soLineItems.reduce((s, r) => s + r.order_qty, 0))}</td>
                      <td className="py-1 text-right font-mono text-amber-300">{fmt(soLineItems.reduce((s, r) => s + r.allocated_qty, 0))}</td>
                      <td className="py-1 text-right font-mono text-emerald-400">{fmt(soLineItems.reduce((s, r) => s + r.balance_qty, 0))}</td>
                    </tr>
                  </tfoot>
                </table>
                <div className="text-[9px] text-zinc-500 mt-1.5 italic">Click any row to pick that Product Code · balance auto-fills the Order Qty below.</div>
              </div>
            )}

            {/* Machines Allotted (Feb 2026) — every loom currently frozen on this WO */}
            {selectedOrderId && (
              <div className="tactical-card p-2.5 border border-[var(--border)]/60" data-testid="planner-allotted-machines">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center justify-between">
                  <span>Machines Allotted · Active plans on this WO</span>
                  <span className="font-mono normal-case tracking-normal text-emerald-400">{allottedMachines.length} loom{allottedMachines.length === 1 ? "" : "s"}</span>
                </div>
                {allottedMachines.length === 0 ? (
                  <div className="text-[11px] text-zinc-500 italic py-2 text-center">No machines allotted yet — pick a model and freeze a plan below.</div>
                ) : (
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-zinc-500 uppercase tracking-widest text-[9px] border-b border-[var(--border)]/60">
                        <th className="text-left py-1 font-medium">Loom</th>
                        <th className="text-left py-1 font-medium">Model</th>
                        <th className="text-left py-1 font-medium">Product Code</th>
                        <th className="text-right py-1 font-medium">Planned Qty</th>
                        <th className="text-left py-1 font-medium">Window</th>
                        <th className="text-center py-1 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allottedMachines.map(m => (
                        <tr key={m.plan_id} data-testid={`planner-allotted-${m.machine_no}`} className="border-b border-[var(--border)]/40 last:border-0 hover:bg-[var(--bg-2)]/40">
                          <td className="py-1 font-mono font-semibold text-white">{m.machine_no}</td>
                          <td className="py-1 font-mono text-zinc-400 truncate max-w-[110px]" title={m.machine_model}>{m.machine_model}</td>
                          <td className="py-1 font-mono text-zinc-300 truncate max-w-[200px]" title={m.article_no}>{m.article_no}</td>
                          <td className="py-1 text-right font-mono text-emerald-400">{fmt(m.planned_qty)}</td>
                          <td className="py-1 font-mono text-[10px] text-zinc-500">{m.start_date || "—"} → {m.end_date || "—"}</td>
                          <td className="py-1 text-center">
                            <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-semibold uppercase tracking-wider ${m.status === "Running" ? "bg-blue-500/20 text-blue-400" : "bg-amber-500/20 text-amber-400"}`}>{m.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Feb 2026 · Production Requirements math (moved into LEFT
                column of the 50/50 split so the SO/WO context, Article
                grid and the per-head production math live together on
                one side). */}
            <div className="tactical-card p-2.5 border border-[var(--border)]/60">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Production Requirements · Math</div>
              <div className="grid grid-cols-3 gap-2">
                <MiniField label="Picks / cm" value={picksPerCm || "—"} testId="planner-picks" />
                <MiniField label="Speed RPM" value={speedRpm || "—"} testId="planner-speed" />
                <MiniField label="Std Eff %" value={stdEff || "—"} testId="planner-eff" />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <div className="overline mb-1">Heads {selectedMachineType && <span className="text-[9px] text-emerald-500 ml-1">from model</span>}</div>
                  <FormattedNumberInput data-testid="planner-heads" min="1" value={heads} onChange={e => setHeads(e.target.value)} className={`${inp} text-right font-mono ${selectedMachineType ? "border-emerald-500/40" : ""}`} />
                </div>
                <div>
                  <div className="overline mb-1">Hours / Day</div>
                  <FormattedNumberInput data-testid="planner-hours" min="1" max="24" value={hoursPerDay} onChange={e => setHoursPerDay(e.target.value)} className={`${inp} text-right font-mono`} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="tactical-card p-2.5 border border-emerald-500/30">
                  <div className="overline text-[9px] mb-0.5">Prod / Hr / Head (m)</div>
                  <div data-testid="planner-prod-hr-head" className="font-mono text-emerald-300 text-lg font-bold">{fmtDec(prodPerHrPerHead)}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">= {speedRpm || 0} ÷ {picksPerCm || 0} × 0.6</div>
                </div>
                <div className="tactical-card p-2.5 border border-[var(--accent)]/40">
                  <div className="overline text-[9px] mb-0.5">Prod / Day / Machine (m)</div>
                  <div data-testid="planner-derived-daily" className="font-mono text-[var(--accent)] text-lg font-bold">{fmt(derivedProdPerDayPerMachine)}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">× {heads}h × {hoursPerDay}h × {stdEff || 100}%</div>
                </div>
              </div>
            </div>
          </div>
          {/* RIGHT column — Free Machine Models funnel, selected Machine
              Model summary + full Machine Availability list with the
              14-day preview strip. */}
          <div className="space-y-2.5">
            {/* Free Machine Models overview — surfaces the moment an SO is
                picked (before the planner narrows to a specific model). Each
                row is clickable and snaps the Machine Model dropdown below. */}
            {selectedOrderId && modelAvailability.length > 0 && (
              <div className="tactical-card p-2.5 border border-emerald-500/25" data-testid="planner-model-availability">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center justify-between">
                  <span>Free Machine Models · Available for Allocation</span>
                  <span className="font-mono normal-case tracking-normal text-emerald-400">
                    {modelAvailability.reduce((s, r) => s + r.free_now, 0)} looms free · {modelAvailability.length} model{modelAvailability.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="max-h-56 overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-[var(--bg-2)]/60">
                      <tr className="uppercase tracking-widest text-[9px] text-zinc-500">
                        <th className="text-left px-2 py-1 font-medium">Model</th>
                        <th className="text-left px-2 py-1 font-medium">Category</th>
                        <th className="text-right px-2 py-1 font-medium">Free Now</th>
                        <th className="text-right px-2 py-1 font-medium">Total</th>
                        <th className="text-left px-2 py-1 font-medium">Next Free</th>
                        <th className="text-right px-2 py-1 font-medium w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {modelAvailability.map(row => {
                        const isPicked = selectedMachineTypeId === row.id;
                        const anyFree = row.free_now > 0;
                        return (
                          <tr
                            key={row.id}
                            data-testid={`planner-model-row-${row.machine_model}`}
                            onClick={() => setSelectedMachineTypeId(row.id)}
                            className={`cursor-pointer border-t border-[var(--border)]/40 ${isPicked ? "bg-[var(--accent)]/15" : "hover:bg-[var(--bg-2)]/40"}`}
                          >
                            <td className="px-2 py-1 font-mono font-semibold text-white truncate max-w-[140px]" title={`${row.machine_model} · ${row.name}`}>
                              {row.machine_model}
                              <div className="text-[9px] text-zinc-500 truncate">{row.name}</div>
                            </td>
                            <td className="px-2 py-1 font-mono text-[10px] text-zinc-400">{row.category}</td>
                            <td className={`px-2 py-1 text-right font-mono font-bold ${anyFree ? "text-emerald-400" : "text-zinc-500"}`}>{row.free_now}</td>
                            <td className="px-2 py-1 text-right font-mono text-zinc-400">{row.total}</td>
                            <td className="px-2 py-1 font-mono text-[10px]">
                              {anyFree
                                ? <span className="text-emerald-400 font-semibold">Free now</span>
                                : <span className="text-amber-400">{row.earliest_free}</span>}
                            </td>
                            <td className="px-2 py-1 text-right">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${isPicked ? "bg-[var(--accent)] text-white" : "bg-[var(--accent)]/15 text-[var(--accent)]"}`}>
                                {isPicked ? "Picked" : "Pick →"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="text-[9px] text-zinc-500 mt-1.5 italic">Click any model to load its loom-level availability below.</div>
              </div>
            )}

            <div>
              <div className="overline mb-1.5">Machine Model</div>
              <Select value={selectedMachineTypeId} onValueChange={setSelectedMachineTypeId}>
                <SelectTrigger data-testid="planner-mm-select" className={selCls}><SelectValue placeholder="Pick a Machine Model…" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-72">
                  {machineTypes
                    .filter(m => m.machine_model)
                    .map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.machine_model} · {m.name} · {m.category}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {selectedMachineType && (
                <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                  {selectedMachineType.no_of_heads} heads · {selectedMachineType.machine_width}mm wide{selectedMachineType.category === "Jacquard Machine" ? ` · ${selectedMachineType.jacquard_hooks} hooks` : ""}
                </div>
              )}
              {selectedMachineTypeId && machineFreeFrom.length > 0 && (
                <div className="mt-2.5 border border-[var(--border)]/60 rounded-sm overflow-hidden" data-testid="planner-free-from-panel">
                  <div className="px-2 py-1.5 bg-[var(--bg-2)]/40 flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
                    <span>Machine Availability · Free From</span>
                    <span className="font-mono normal-case tracking-normal text-emerald-400">
                      {machineFreeFrom.filter(x => x.is_free_now).length} free now · {machineFreeFrom.length - machineFreeFrom.filter(x => x.is_free_now).length} scheduled
                    </span>
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className="sticky top-0 bg-[var(--bg-2)]/60">
                        <tr className="uppercase tracking-widest text-[9px] text-zinc-500">
                          <th className="text-left px-2 py-1 font-medium">Loom</th>
                          <th className="text-left px-2 py-1 font-medium">Free From</th>
                          <th className="text-left px-2 py-1 font-medium">Busy Until</th>
                          <th className="text-left px-2 py-1 font-medium">Current SO</th>
                          <th className="text-left px-2 py-1 font-medium" title="Next 14 days · green=free · red=occupied">Next 14 d</th>
                          <th className="text-right px-2 py-1 font-medium w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {machineFreeFrom.map(row => (
                          <tr key={row.machine_id} data-testid={`planner-free-row-${row.machine_no}`} className="border-t border-[var(--border)]/40 hover:bg-[var(--bg-2)]/40">
                            <td className="px-2 py-1 font-mono font-semibold text-white">{row.machine_no}</td>
                            <td className="px-2 py-1 font-mono">
                              {row.is_free_now ? (
                                <span className="text-emerald-400 font-semibold">Free now</span>
                              ) : (
                                <span className="text-amber-400">{row.free_from}</span>
                              )}
                            </td>
                            <td className="px-2 py-1 font-mono text-zinc-500 text-[10px]">{row.latest_end || "—"}</td>
                            <td className="px-2 py-1 font-mono text-zinc-500 text-[10px] truncate max-w-[110px]">{row.busy_until_so || "—"}</td>
                            <td className="px-2 py-1">
                              <div className="flex gap-[2px]" data-testid={`planner-strip-${row.machine_no}`}>
                                {row.strip.map((c, i) => (
                                  <span
                                    key={c.day}
                                    title={`${c.day} · ${c.busy ? "BUSY" : "FREE"}`}
                                    className={`w-2.5 h-3 rounded-[1px] ${c.busy ? "bg-red-500/70" : "bg-emerald-500/60"} ${i === 0 ? "ring-1 ring-white/50" : ""}`}
                                  />
                                ))}
                              </div>
                            </td>
                            <td className="px-2 py-1 text-right">
                              <button
                                onClick={() => setStartDate(row.free_from)}
                                data-testid={`planner-use-date-${row.machine_no}`}
                                title={`Set start date to ${row.free_from}`}
                                className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[var(--accent)]/15 text-[var(--accent)] hover:bg-[var(--accent)]/25 font-semibold"
                              >Use →</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stage 3 · Machine Availability & Freeze */}
      {selectedMachineType && (
        <div className="tactical-card p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="overline mb-0.5">Machine Availability & Allocation</div>
              <div className="text-lg font-display font-black text-white">Freeze Capacity for this SO + Product</div>
              <div className="text-xs text-zinc-500 mt-0.5">Idle looms matching <span className="font-mono text-white">{selectedMachineType.machine_model}</span> · alternates from the same category are listed below. Select and freeze — that many Production Plans will be created.</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Changeover {matchedItem && <span className="text-emerald-400 lowercase tracking-normal font-medium">from master</span>}</div>
              <FormattedNumberInput data-testid="planner-changeover" min="0" step="0.25" value={changeoverHours} onChange={e => setChangeoverHours(e.target.value)} className={`h-8 w-16 bg-[var(--bg-0)] border ${matchedItem ? "border-emerald-500/40" : "border-yellow-500/40"} px-2 rounded-sm text-xs text-right font-mono`} />
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Setup {matchedItem && <span className="text-emerald-400 lowercase tracking-normal font-medium">from master</span>}</div>
              <FormattedNumberInput data-testid="planner-setup" min="0" step="0.25" value={setupHours} onChange={e => setSetupHours(e.target.value)} className={`h-8 w-16 bg-[var(--bg-0)] border ${matchedItem ? "border-emerald-500/40" : "border-orange-500/40"} px-2 rounded-sm text-xs text-right font-mono`} />
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Start</div>
              <input data-testid="planner-start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs" />
              <button
                data-testid="planner-freeze"
                onClick={freezeCapacity}
                disabled={freezing || selectedIds.length === 0 || overCap > 0}
                title={overCap > 0 ? `Over-allocated by ${fmt(overCap)} m — reduce to at most ${fmt(effectiveCap)} m` : ""}
                className="h-8 px-3 bg-[var(--accent)] hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold rounded-sm flex items-center gap-1.5"
              >
                <LockIcon size={12} weight="bold" />{freezing ? "Freezing…" : overCap > 0 ? `Over-allocated by ${fmt(overCap)} m` : `Freeze on ${selectedIds.length || 0} loom${selectedIds.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>

          {/* Feb 2026 · Universal Over-Allocation Guard — prominent red
              alert banner. Visible the instant `totalAllocated` exceeds
              the effective cap (per-Article balance, or SO Total Balance
              for head-split flows). Uses the exact user-facing message
              format so operators see the same phrasing that the backend
              raises on the freeze POST. */}
          {overCap > 0 && (
            <div
              data-testid="planner-overcap-banner"
              className="mb-4 rounded-sm border border-red-500/60 bg-red-500/10 px-3 py-2.5 text-[12px] flex items-start gap-2"
            >
              <WarningCircleIcon size={16} weight="fill" className="text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 leading-snug">
                <div className="font-semibold text-red-200">
                  Error: Cannot allocate {fmt(totalAllocated)}m. Only {fmt(effectiveCap)} meters remain unallocated for this Work Order.
                </div>
                <div className="text-[11px] text-red-300/80 mt-0.5 font-mono">
                  Over by <span className="font-bold">{fmt(overCap)} m</span> · reduce split qty or click <span className="text-emerald-300">Recalculate Balance</span> above to refresh.
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] font-semibold text-white uppercase tracking-widest mb-2 flex items-center gap-2">
                Idle · Exact Model
                <span className="text-emerald-400 font-mono">{idleMatching.length}</span>
                <span className="text-zinc-500 text-[10px] font-mono">/ {matchingMachines.length}</span>
              </div>
              {freezeListMatching.length === 0 && <div className="text-xs text-zinc-500 italic py-2">No idle looms of this exact model.</div>}
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {freezeListMatching.map(m => {
                  const busy = busyPlanFor(m.id, startDate, estimatedFinish);
                  const queued = !!perMachineStart[m.id];
                  const disabled = !!busy && !queued;
                  return (
                    <React.Fragment key={m.id}>
                    <label data-testid={`planner-mach-${m.code}`} className={`flex items-center gap-2.5 p-2 rounded-sm border ${queued ? "border-blue-500/50 bg-blue-500/10 cursor-pointer" : disabled ? "border-orange-500/30 bg-orange-500/5 opacity-80" : "border-[var(--border)]/60 hover:border-emerald-500/40 hover:bg-emerald-500/5 cursor-pointer"}`}>
                      <input type="checkbox" checked={selectedMachineIds.has(m.id)} disabled={disabled} onChange={() => !disabled && toggleMachine(m.id)} className="accent-[var(--accent)]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-white text-xs truncate">{m.code} <span className="text-emerald-300 ml-1">{m.machine_model || selectedMachineType?.machine_model || "—"}</span></div>
                        <div className="text-[10px] text-zinc-500 truncate">{m.machine_type} · {m.brand}</div>
                        {selectedMachineIds.has(m.id) && !hasSplit(m.id) && !disabled && (() => {
                          const q = qtyFor(m.id);
                          const d = machineDaysForQty(m, q);
                          const daily = machineDailyOutput(m);
                          const fin = machineFinishFor(m, startFor(m), q);
                          if (!d) return null;
                          return (
                            <div data-testid={`planner-mach-eta-${m.code}`} className="text-[10px] text-emerald-300 mt-0.5 truncate font-mono">
                              ≈ {d}d @ {headsForMachine(m)}h · {fmt(Math.round(daily))} m/day · finish {fin}
                            </div>
                          );
                        })()}
                        {queued && <div className="text-[10px] text-blue-300 mt-0.5 truncate">QUEUED · starts {perMachineStart[m.id]} (after {busy?.planned_finish || "current plan"})</div>}
                        {queued && <div className="text-[9px] text-zinc-500 italic truncate">Change SO / Product above · then Freeze for a new order on this loom</div>}
                        {busy && !queued && <div className="text-[10px] text-orange-400 mt-0.5 truncate">OCCUPIED · frozen until {busy.planned_finish}</div>}
                        {busy && !queued && (
                          <MachineTimelinePeek
                            startISO={startDate}
                            plans={activePlans.filter(p => p.machine_id === m.id)}
                          />
                        )}
                      </div>
                      {busy && !queued && (
                        <button
                          type="button"
                          data-testid={`planner-queue-${m.code}`}
                          onClick={(e) => { e.preventDefault(); queueMachineAfter(m.id, busy.planned_finish); }}
                          className="h-7 px-2 text-[10px] font-semibold rounded-sm bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 border border-blue-500/40"
                          title={`Schedule this order back-to-back — starts day after ${busy.planned_finish}. You can switch to a different SO + Product before freezing.`}
                        >Queue after →</button>
                      )}
                      {queued && (
                        <button
                          type="button"
                          data-testid={`planner-cancel-queue-${m.code}`}
                          onClick={(e) => { e.preventDefault(); cancelQueue(m.id); }}
                          className="h-7 px-2 text-[10px] font-semibold rounded-sm bg-zinc-500/15 text-zinc-400 hover:bg-zinc-500/25 border border-zinc-500/40"
                          title="Cancel queued back-to-back allocation"
                        >Cancel</button>
                      )}
                      {selectedMachineIds.has(m.id) && !hasSplit(m.id) && !disabled && (
                        <button
                          type="button"
                          data-testid={`planner-split-${m.code}`}
                          onClick={(e) => { e.preventDefault(); enableHeadSplit(m); }}
                          className="h-7 px-2 text-[10px] font-semibold rounded-sm bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 border border-purple-500/40"
                          title={`Split ${headsForMachine(m)} heads across multiple article variants running in parallel`}
                        >Split heads →</button>
                      )}
                      {selectedMachineIds.has(m.id) && (() => {
                        const eff = startFor(m);
                        const q = qtyFor(m.id);
                        const fin = machineFinishFor(m, eff, q);
                        const cf = conflictForMachine(m, eff, fin);
                        const cfOrderNo = cf ? (orders.find(o => o.id === cf.order_id)?.order_no || (cf.order_id || "").slice(0, 6)) : "";
                        return (
                          <div className="flex flex-col items-end gap-0.5" data-testid={`planner-start-wrap-${m.code}`}>
                            <input
                              type="date"
                              data-testid={`planner-start-${m.code}`}
                              value={eff}
                              min={todayISO}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPerMachineStart(s => ({ ...s, [m.id]: v }));
                                // Blur so Safari/Chrome closes the native
                                // calendar overlay after selection instead
                                // of keeping it pinned to the re-rendered
                                // input.
                                e.target.blur();
                              }}
                              title={`Proposed start · default is ${defaultStartFor(m)}`}
                              className={`w-32 h-7 px-2 bg-[var(--bg-0)] border rounded-sm text-[11px] font-mono text-white focus:outline-none ${cf ? "border-red-500/60 focus:border-red-400" : "border-[var(--border)] focus:border-[var(--accent)]"}`}
                            />
                            {cf && (
                              <div
                                data-testid={`planner-start-conflict-${m.code}`}
                                className="text-[9px] text-red-400 leading-tight max-w-[128px] whitespace-normal text-right"
                              >
                                ⚠ Conflict with {cfOrderNo || "existing plan"} — overlap?
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <FormattedNumberInput
                        min="0"
                        data-testid={`planner-qty-${m.code}`}
                        disabled={!selectedMachineIds.has(m.id) || disabled || hasSplit(m.id)}
                        value={hasSplit(m.id) ? splitQtySum(m.id) : (selectedMachineIds.has(m.id) ? (perMachineQty[m.id] ?? evenSplitQty) : "")}
                        onChange={e => setQtyFor(m.id, e.target.value)}
                        onClick={e => e.preventDefault()}
                        placeholder="qty"
                        className="w-24 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-xs font-mono text-right disabled:opacity-40 focus:outline-none focus:border-[var(--accent)]"
                      />
                      <StatusBadge status={queued ? "Planned" : disabled ? "Occupied" : m.status} />
                    </label>
                    {selectedMachineIds.has(m.id) && hasSplit(m.id) && (
                      <HeadSplitEditor
                        machine={m}
                        totalHeads={headsForMachine(m)}
                        rows={perMachineSplits[m.id] || []}
                        orderArticles={orderArticles}
                        onAdd={() => addSplitVariant(m.id)}
                        onRemove={(idx) => removeSplitVariant(m.id, idx)}
                        onUpdate={(idx, patch) => updateSplit(m.id, idx, patch)}
                        onDisable={() => disableHeadSplit(m.id)}
                        sumHeads={splitHeadsSum(m.id)}
                        sumQty={splitQtySum(m.id)}
                      />
                    )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-white uppercase tracking-widest mb-2 flex items-center gap-2">
                Idle · Compatible Alternates
                <span className="text-amber-400 font-mono">{idleAlternates.length}</span>
                <span className="text-zinc-500 text-[10px] font-mono">same {selectedMachineType.category}</span>
              </div>
              {freezeListAlternates.length === 0 && <div className="text-xs text-zinc-500 italic py-2">No idle alternates in the same category.</div>}
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {freezeListAlternates.map(m => {
                  const alt = machineTypes.find(t => t.id === m.machine_type_id);
                  const busy = busyPlanFor(m.id, startDate, estimatedFinish);
                  const queued = !!perMachineStart[m.id];
                  const disabled = !!busy && !queued;
                  return (
                    <React.Fragment key={m.id}>
                    <label data-testid={`planner-alt-${m.code}`} className={`flex items-center gap-2.5 p-2 rounded-sm border ${queued ? "border-blue-500/50 bg-blue-500/10 cursor-pointer" : disabled ? "border-orange-500/30 bg-orange-500/5 opacity-80" : "border-[var(--border)]/60 hover:border-amber-500/40 hover:bg-amber-500/5 cursor-pointer"}`}>
                      <input type="checkbox" checked={selectedMachineIds.has(m.id)} disabled={disabled} onChange={() => !disabled && toggleMachine(m.id)} className="accent-[var(--accent)]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-white text-xs truncate">{m.code} <span className="text-amber-300 ml-1">{alt?.machine_model || m.machine_model || "—"}</span></div>
                        <div className="text-[10px] text-zinc-500 truncate">{alt?.name || m.brand} · {m.machine_type}</div>
                        {selectedMachineIds.has(m.id) && !hasSplit(m.id) && !disabled && (() => {
                          const q = qtyFor(m.id);
                          const d = machineDaysForQty(m, q);
                          const daily = machineDailyOutput(m);
                          const fin = machineFinishFor(m, startFor(m), q);
                          if (!d) return null;
                          return (
                            <div data-testid={`planner-alt-eta-${m.code}`} className="text-[10px] text-amber-300 mt-0.5 truncate font-mono">
                              ≈ {d}d @ {headsForMachine(m)}h · {fmt(Math.round(daily))} m/day · finish {fin}
                            </div>
                          );
                        })()}
                        {queued && <div className="text-[10px] text-blue-300 mt-0.5 truncate">QUEUED · starts {perMachineStart[m.id]} (after {busy?.planned_finish || "current plan"})</div>}
                        {queued && <div className="text-[9px] text-zinc-500 italic truncate">Change SO / Product above · then Freeze for a new order on this loom</div>}
                        {busy && !queued && <div className="text-[10px] text-orange-400 mt-0.5 truncate">OCCUPIED · frozen until {busy.planned_finish}</div>}
                        {busy && !queued && (
                          <MachineTimelinePeek
                            startISO={startDate}
                            plans={activePlans.filter(p => p.machine_id === m.id)}
                          />
                        )}
                      </div>
                      {busy && !queued && (
                        <button
                          type="button"
                          data-testid={`planner-queue-${m.code}`}
                          onClick={(e) => { e.preventDefault(); queueMachineAfter(m.id, busy.planned_finish); }}
                          className="h-7 px-2 text-[10px] font-semibold rounded-sm bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 border border-blue-500/40"
                          title={`Schedule this order back-to-back — starts day after ${busy.planned_finish}. You can switch to a different SO + Product before freezing.`}
                        >Queue after →</button>
                      )}
                      {queued && (
                        <button
                          type="button"
                          data-testid={`planner-cancel-queue-${m.code}`}
                          onClick={(e) => { e.preventDefault(); cancelQueue(m.id); }}
                          className="h-7 px-2 text-[10px] font-semibold rounded-sm bg-zinc-500/15 text-zinc-400 hover:bg-zinc-500/25 border border-zinc-500/40"
                          title="Cancel queued back-to-back allocation"
                        >Cancel</button>
                      )}
                      {selectedMachineIds.has(m.id) && !hasSplit(m.id) && !disabled && (
                        <button
                          type="button"
                          data-testid={`planner-split-${m.code}`}
                          onClick={(e) => { e.preventDefault(); enableHeadSplit(m); }}
                          className="h-7 px-2 text-[10px] font-semibold rounded-sm bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 border border-purple-500/40"
                          title={`Split ${headsForMachine(m)} heads across multiple article variants running in parallel`}
                        >Split heads →</button>
                      )}
                      {selectedMachineIds.has(m.id) && (() => {
                        const eff = startFor(m);
                        const q = qtyFor(m.id);
                        const fin = machineFinishFor(m, eff, q);
                        const cf = conflictForMachine(m, eff, fin);
                        const cfOrderNo = cf ? (orders.find(o => o.id === cf.order_id)?.order_no || (cf.order_id || "").slice(0, 6)) : "";
                        return (
                          <div className="flex flex-col items-end gap-0.5" data-testid={`planner-start-wrap-${m.code}`}>
                            <input
                              type="date"
                              data-testid={`planner-start-${m.code}`}
                              value={eff}
                              min={todayISO}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPerMachineStart(s => ({ ...s, [m.id]: v }));
                                // Blur so Safari/Chrome closes the native
                                // calendar overlay after selection instead
                                // of keeping it pinned to the re-rendered
                                // input.
                                e.target.blur();
                              }}
                              title={`Proposed start · default is ${defaultStartFor(m)}`}
                              className={`w-32 h-7 px-2 bg-[var(--bg-0)] border rounded-sm text-[11px] font-mono text-white focus:outline-none ${cf ? "border-red-500/60 focus:border-red-400" : "border-[var(--border)] focus:border-[var(--accent)]"}`}
                            />
                            {cf && (
                              <div
                                data-testid={`planner-start-conflict-${m.code}`}
                                className="text-[9px] text-red-400 leading-tight max-w-[128px] whitespace-normal text-right"
                              >
                                ⚠ Conflict with {cfOrderNo || "existing plan"} — overlap?
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <FormattedNumberInput
                        min="0"
                        data-testid={`planner-qty-${m.code}`}
                        disabled={!selectedMachineIds.has(m.id) || disabled || hasSplit(m.id)}
                        value={hasSplit(m.id) ? splitQtySum(m.id) : (selectedMachineIds.has(m.id) ? (perMachineQty[m.id] ?? evenSplitQty) : "")}
                        onChange={e => setQtyFor(m.id, e.target.value)}
                        onClick={e => e.preventDefault()}
                        placeholder="qty"
                        className="w-24 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-xs font-mono text-right disabled:opacity-40 focus:outline-none focus:border-[var(--accent)]"
                      />
                      <StatusBadge status={queued ? "Planned" : disabled ? "Occupied" : m.status} />
                    </label>
                    {selectedMachineIds.has(m.id) && hasSplit(m.id) && (
                      <HeadSplitEditor
                        machine={m}
                        totalHeads={headsForMachine(m)}
                        rows={perMachineSplits[m.id] || []}
                        orderArticles={orderArticles}
                        onAdd={() => addSplitVariant(m.id)}
                        onRemove={(idx) => removeSplitVariant(m.id, idx)}
                        onUpdate={(idx, patch) => updateSplit(m.id, idx, patch)}
                        onDisable={() => disableHeadSplit(m.id)}
                        sumHeads={splitHeadsSum(m.id)}
                        sumQty={splitQtySum(m.id)}
                      />
                    )}
                  </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          {selectedIds.length > 0 && (
            <div data-testid="planner-freeze-preview" className={`mt-4 p-3 border rounded-sm text-xs flex flex-wrap items-center gap-2 ${overCap === 0 ? (allocationDelta === 0 ? "border-[var(--accent)]/40 bg-[var(--accent)]/10 text-zinc-200" : "border-amber-500/40 bg-amber-500/10 text-amber-200") : "border-red-500/40 bg-red-500/10 text-red-200"}`}>
              <SparkleIcon size={14} className="text-[var(--accent)] flex-shrink-0" weight="fill" />
              <span>
                Allocating <b>{fmt(totalAllocated)}</b> m across <b>{selectedIds.length}</b> loom{selectedIds.length === 1 ? "" : "s"} · {capSource} <b>{fmt(effectiveCap)}</b> m
                {overCap === 0 && totalAllocated === effectiveCap && <span className="ml-2 text-emerald-300 font-semibold">· fully covered</span>}
                {overCap === 0 && totalAllocated < effectiveCap && <span className="ml-2 font-semibold">· short by {fmt(effectiveCap - totalAllocated)} m</span>}
                {overCap > 0 && <span className="ml-2 font-semibold">· BLOCKED — over-allocated by {fmt(overCap)} m (max {fmt(effectiveCap)} m · {capSource})</span>}
              </span>
              <button onClick={resetAllocations} className="ml-auto h-6 px-2 border border-[var(--border)] hover:bg-[var(--bg-2)] text-[10px] rounded-sm text-zinc-400" data-testid="planner-reset-alloc">Reset to even split</button>
            </div>
          )}

          {/* Frozen plans list — grouped by loom · shards on the same loom collapsed into one row */}
          {frozenForCurrent.length > 0 && (
            <div className="mt-5 border-t border-[var(--border)]/60 pt-4">
              <div className="text-[11px] font-semibold text-white uppercase tracking-widest mb-2 flex items-center gap-2">
                <LockIcon size={12} weight="bold" className="text-emerald-400" />
                Currently Frozen for this SO
                <span className="text-emerald-400 font-mono">{frozenForCurrent.length} loom{frozenForCurrent.length === 1 ? "" : "s"}</span>
              </div>
              <div className="space-y-1.5">
                {frozenForCurrent.map(g => {
                  const isSplit = g.plans.length > 1;
                  return (
                    <div key={g.machine_id} data-testid={`planner-frozen-${g.machine_code}`} className={`p-2 rounded-sm border ${g.has_current ? "border-emerald-500/40 bg-emerald-500/10" : "border-emerald-500/20 bg-emerald-500/5"} text-xs`}>
                      <div className="flex items-center gap-3">
                        <LockIcon size={12} weight="fill" className="text-emerald-400 flex-shrink-0" />
                        <div className="font-mono text-white flex-1 min-w-0 truncate">
                          {g.machine_code} {g.machine_model && <span className="text-emerald-300 ml-1">{g.machine_model}</span>} · {fmt(g.total_qty)} m · {g.start} → {g.finish}
                        </div>
                        {isSplit && (
                          <span
                            data-testid={`planner-frozen-shards-${g.machine_code}`}
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-purple-500/20 text-purple-300 border border-purple-500/40"
                            title={`Head-split on this loom · ${g.total_heads} heads used`}
                          >
                            +{g.plans.length} shards · {g.total_heads} heads
                          </span>
                        )}
                        {!isSplit && <StatusBadge status={g.plans[0].status} />}
                      </div>
                      {isSplit ? (
                        <div className="mt-1.5 ml-6 space-y-1">
                          {g.plans.map(p => {
                            const item = items.find(i => i.id === p.item_id);
                            const article = item?.description || item?.article_no || p.item_id?.slice(-8) || "—";
                            const isCurrent = matchedItem?.id === p.item_id;
                            return (
                              <div
                                key={p.id}
                                data-testid={`planner-frozen-shard-${g.machine_code}-${p.id.slice(-6)}`}
                                className={`flex items-center gap-2 p-1.5 rounded-sm border ${isCurrent ? "border-[var(--accent)]/40 bg-[var(--accent)]/10" : "border-[var(--border)]/40"} text-[11px]`}
                              >
                                <span className="font-mono text-purple-300 text-[10px] w-10 flex-shrink-0">{p.heads_used || "?"}h</span>
                                <span className="font-mono text-zinc-300 flex-1 truncate" title={article}>{isCurrent && <span className="text-[var(--accent)] mr-1">▸</span>}{article}</span>
                                <span className="font-mono text-emerald-300">{fmt(p.planned_qty)} m</span>
                                <StatusBadge status={p.status} />
                                <FormattedNumberInput
                                  min="1"
                                  placeholder="add m"
                                  data-testid={`planner-add-input-${g.machine_code}-${p.id.slice(-6)}`}
                                  value={addInputs[p.id] ?? ""}
                                  onChange={(e) => setAddInputs(r => ({ ...r, [p.id]: e.target.value }))}
                                  className="w-20 h-6 px-1.5 bg-[var(--bg-0)] border border-emerald-500/30 rounded-sm text-white text-[10px] font-mono text-right"
                                />
                                <button
                                  data-testid={`planner-add-${g.machine_code}-${p.id.slice(-6)}`}
                                  onClick={() => addPlanQty(p.id)}
                                  disabled={!addInputs[p.id]}
                                  className="h-6 px-2 border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 text-[10px] rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Add these meters to the plan — planned finish extends proportionally"
                                >+ Add</button>
                                <FormattedNumberInput
                                  min="1"
                                  max={p.planned_qty}
                                  placeholder="release m"
                                  data-testid={`planner-release-input-${g.machine_code}-${p.id.slice(-6)}`}
                                  value={releaseInputs[p.id] ?? ""}
                                  onChange={(e) => setReleaseInputs(r => ({ ...r, [p.id]: e.target.value }))}
                                  className="w-20 h-6 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-[10px] font-mono text-right"
                                />
                                <button
                                  data-testid={`planner-release-${g.machine_code}-${p.id.slice(-6)}`}
                                  onClick={() => releasePlanQty(p.id, +p.planned_qty)}
                                  disabled={!releaseInputs[p.id]}
                                  className="h-6 px-2 border border-blue-500/40 hover:bg-blue-500/10 text-blue-300 text-[10px] rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Release this many meters back to SO balance"
                                >Release</button>
                                <button
                                  data-testid={`planner-unfreeze-${g.machine_code}-${p.id.slice(-6)}`}
                                  onClick={() => unfreezePlan(p.id)}
                                  className="h-6 px-2 border border-red-500/40 hover:bg-red-500/10 text-red-300 text-[10px] rounded-sm flex items-center gap-1"
                                  title="Unfreeze this shard"
                                >
                                  <TrashIcon size={10} />Unfreeze
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="mt-1 ml-6 flex items-center gap-2">
                          {(() => {
                            const p = g.plans[0];
                            const item = items.find(i => i.id === p.item_id);
                            const article = item?.description || item?.article_no || p.item_id?.slice(-8) || "—";
                            return (
                              <>
                                <span className="font-mono text-zinc-400 text-[10px] flex-1 truncate" title={article}>{article}</span>
                                <span className="font-mono text-emerald-300 text-[10px]">{fmt(p.planned_qty)} m</span>
                                <FormattedNumberInput
                                  min="1"
                                  placeholder="add m"
                                  data-testid={`planner-add-input-${g.machine_code}`}
                                  value={addInputs[p.id] ?? ""}
                                  onChange={(e) => setAddInputs(r => ({ ...r, [p.id]: e.target.value }))}
                                  className="w-20 h-6 px-1.5 bg-[var(--bg-0)] border border-emerald-500/30 rounded-sm text-white text-[10px] font-mono text-right"
                                />
                                <button
                                  data-testid={`planner-add-${g.machine_code}`}
                                  onClick={() => addPlanQty(p.id)}
                                  disabled={!addInputs[p.id]}
                                  className="h-6 px-2 border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 text-[10px] rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Add these meters to the plan — planned finish extends proportionally"
                                >+ Add</button>
                                <FormattedNumberInput
                                  min="1"
                                  max={p.planned_qty}
                                  placeholder="release m"
                                  data-testid={`planner-release-input-${g.machine_code}`}
                                  value={releaseInputs[p.id] ?? ""}
                                  onChange={(e) => setReleaseInputs(r => ({ ...r, [p.id]: e.target.value }))}
                                  className="w-20 h-6 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-[10px] font-mono text-right"
                                />
                                <button
                                  data-testid={`planner-release-${g.machine_code}`}
                                  onClick={() => releasePlanQty(p.id, +p.planned_qty)}
                                  disabled={!releaseInputs[p.id]}
                                  className="h-6 px-2 border border-blue-500/40 hover:bg-blue-500/10 text-blue-300 text-[10px] rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Release this many meters back to SO balance so you can allocate on another loom"
                                >Release</button>
                                <button
                                  data-testid={`planner-unfreeze-${g.machine_code}`}
                                  onClick={() => unfreezePlan(p.id)}
                                  className="h-6 px-2 border border-red-500/40 hover:bg-red-500/10 text-red-300 text-[10px] rounded-sm flex items-center gap-1"
                                  title="Unfreeze this plan"
                                >
                                  <TrashIcon size={10} />Unfreeze
                                </button>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stage 2 · Order-vs-Machine sizing */}
      <div className="tactical-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline mb-0.5">Capacity Planning</div>
            <div className="text-lg font-display font-black text-white">Order → Machine Requirement Calculator</div>
            <div className="text-xs text-zinc-500 mt-0.5">Order Qty & Delivery Days flow in from the picked SO. Prod-per-Machine is derived from Stage 1 (edit to override).</div>
          </div>
          <button
            data-testid="planner-reset"
            onClick={resetCalculator}
            className="h-8 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs rounded-sm text-zinc-400"
          >Reset</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <div>
            <Row label="Order Qty (meters)">
              <FormattedNumberInput data-testid="planner-order-qty" min="0" value={orderQty} onChange={e => setOrderQty(e.target.value)} className={`${inp} text-right font-mono w-40 ml-auto`} />
            </Row>
            <Row label="Customer Expected Delivery Days">
              <FormattedNumberInput data-testid="planner-delivery-days" min="1" value={deliveryDays} onChange={e => setDeliveryDays(e.target.value)} className={`${inp} text-right font-mono w-40 ml-auto`} />
            </Row>
            <Row label="Required Production per Day (mtrs)">
              <div data-testid="planner-required-per-day" className="font-mono text-white text-right">{fmt(reqPerDay)}</div>
            </Row>
            <Row label="Production per Day per Machine (mtrs)" tone="bg-amber-500/10 -mx-2 px-2 rounded-sm">
              <FormattedNumberInput data-testid="planner-prod-per-machine" min="0" value={prodPerMachine} onChange={e => setProdPerMachine(e.target.value)} className={`${inp} text-right font-mono w-40 ml-auto bg-amber-500/5 border-amber-500/40`} />
            </Row>
          </div>
          <div>
            <Row label="Number of Machines Required">
              <div data-testid="planner-machines-required" className={`font-mono text-right ${shortfall > 0 ? "text-red-400 font-bold" : "text-white"}`}>{fmt(machinesNeeded)}</div>
            </Row>
            <Row label="Available Machines">
              <FormattedNumberInput data-testid="planner-available-machines" min="0" value={availableMachines} onChange={e => setAvailableMachines(e.target.value)} className={`${inp} text-right font-mono w-40 ml-auto border-2 border-[var(--accent)]`} />
            </Row>
            <Row label="Allotted Capacity per Day (mtrs)">
              <div data-testid="planner-allotted-capacity" className="font-mono text-white text-right">{fmt(allottedCapacity)}</div>
            </Row>
            <Row label="Actual Days Required to Complete" tone="mt-1">
              <div data-testid="planner-actual-days" className={`font-mono text-right text-lg font-bold ${meetsDue ? "text-emerald-400" : "text-red-400"}`}>{fmt(actualDays)}</div>
            </Row>
          </div>
        </div>

        {/* Verdict */}
        <div className={`mt-5 p-3 rounded-sm border text-xs flex items-center gap-2 ${meetsDue ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-red-500/40 bg-red-500/10 text-red-300"}`}>
          {meetsDue ? <CheckCircleIcon size={16} weight="bold" /> : <WarningCircleIcon size={16} weight="bold" />}
          {meetsDue ? (
            <span data-testid="planner-verdict">
              On track — {availableMachines} machine{num(availableMachines) === 1 ? "" : "s"} can complete {fmt(orderQty)} m in <b>{actualDays} day{actualDays === 1 ? "" : "s"}</b> (deadline {deliveryDays}).
            </span>
          ) : (
            <span data-testid="planner-verdict">
              Shortfall — need <b>{shortfall > 0 ? shortfall : 0} more machine{shortfall === 1 ? "" : "s"}</b> or {actualDays - num(deliveryDays)} extra day{Math.abs(actualDays - num(deliveryDays)) === 1 ? "" : "s"} to meet the {deliveryDays}-day deadline.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact read-only tile used inside the source picker
function MiniField({ label, value, testId }) {
  return (
    <div className="tactical-card p-2">
      <div className="overline text-[9px] mb-0.5">{label}</div>
      <div data-testid={testId} className="font-mono text-white text-sm font-semibold">{value}</div>
    </div>
  );
}

function MiniStat({ label, value, unit, color = "text-white" }) {
  return (
    <div className="tactical-card p-3">
      <div className="overline text-[9px] mb-1">{label}</div>
      <div className={`font-mono text-xl font-semibold ${color}`}>{value}{unit && <span className="text-[10px] text-zinc-500 ml-1">{unit}</span>}</div>
    </div>
  );
}

// ── Head Split Editor ────────────────────────────────────────────────
// Inline mini-table that appears under a selected loom once the planner
// clicks "Split heads →". Lets the planner run multiple article/colour
// variants of the same base article in parallel on the SAME loom — 2 heads
// of one variant, 4 heads of another, etc. Enforces sumHeads === totalHeads
// visually so the freeze pre-flight can validate on click.
function HeadSplitEditor({ machine, totalHeads, rows, orderArticles, onAdd, onRemove, onUpdate, onDisable, sumHeads, sumQty }) {
  const balanced = totalHeads > 0 && sumHeads === totalHeads;
  const over = sumHeads > totalHeads;
  const under = totalHeads > 0 && sumHeads < totalHeads;
  return (
    <div
      data-testid={`planner-split-editor-${machine.code}`}
      className={`ml-6 mr-1 mb-1.5 p-2.5 rounded-sm border ${balanced ? "border-purple-500/50 bg-purple-500/5" : over ? "border-red-500/50 bg-red-500/5" : "border-amber-500/40 bg-amber-500/5"}`}
    >
      <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-widest">
        <div className="flex items-center gap-2">
          <span className="text-purple-300 font-semibold">Head Split · {machine.code}</span>
          <span className={`font-mono normal-case tracking-normal ${balanced ? "text-emerald-400" : over ? "text-red-400" : "text-amber-400"}`}>
            {sumHeads}/{totalHeads} heads · {sumQty.toLocaleString("en-IN")} m total
          </span>
        </div>
        <button
          type="button"
          data-testid={`planner-split-close-${machine.code}`}
          onClick={onDisable}
          className="text-[10px] px-2 h-6 border border-zinc-500/40 text-zinc-400 hover:bg-zinc-500/15 rounded-sm normal-case tracking-normal"
          title="Turn off head split and go back to a single-variant allocation"
        >Close split</button>
      </div>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-zinc-500 uppercase tracking-widest text-[9px] border-b border-[var(--border)]/40">
            <th className="text-left py-1 font-medium">Product Code</th>
            <th className="text-right py-1 font-medium w-20">Heads</th>
            <th className="text-right py-1 font-medium w-28">Qty (m)</th>
            <th className="text-right py-1 font-medium w-10"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className="border-b border-[var(--border)]/30 last:border-0">
              <td className="py-1 pr-2">
                <select
                  data-testid={`planner-split-article-${machine.code}-${idx}`}
                  value={r.article_no || ""}
                  onChange={e => onUpdate(idx, { article_no: e.target.value })}
                  className="w-full h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-[11px] font-mono"
                >
                  {(orderArticles.length === 0) && <option value="">(no SO articles)</option>}
                  {orderArticles.map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
                {!r.item_id && r.article_no && (
                  <div className="text-[9px] text-amber-400 mt-0.5">Not in Product Master</div>
                )}
              </td>
              <td className="py-1 text-right">
                <FormattedNumberInput
                  min="0"
                  max={totalHeads || undefined}
                  data-testid={`planner-split-heads-${machine.code}-${idx}`}
                  value={r.heads ?? ""}
                  onChange={e => onUpdate(idx, { heads: +e.target.value })}
                  className="w-16 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-[11px] font-mono text-right"
                />
              </td>
              <td className="py-1 text-right">
                <FormattedNumberInput
                  min="0"
                  data-testid={`planner-split-qty-${machine.code}-${idx}`}
                  value={r.qty ?? ""}
                  onChange={e => onUpdate(idx, { qty: +e.target.value })}
                  className="w-24 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-[11px] font-mono text-right"
                />
              </td>
              <td className="py-1 text-right">
                <button
                  type="button"
                  data-testid={`planner-split-remove-${machine.code}-${idx}`}
                  onClick={onRemove.bind(null, idx)}
                  className="h-6 w-6 text-red-400 hover:bg-red-500/15 rounded-sm text-[11px] font-semibold"
                  title="Remove this variant"
                >×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between mt-2">
        <button
          type="button"
          data-testid={`planner-split-add-${machine.code}`}
          onClick={onAdd}
          disabled={rows.length >= orderArticles.length}
          className="h-7 px-2.5 text-[10px] font-semibold rounded-sm bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 border border-purple-500/40 disabled:opacity-40 disabled:cursor-not-allowed"
          title={rows.length >= orderArticles.length
            ? "All attached Work Order articles are already placed. Attach another compatible WO at the top (Additional WOs · Split-Head) to add more variants."
            : "Add another Work Order's article as a variant to this loom"}
        >+ Add WO variant</button>
        <div className={`text-[10px] font-mono ${balanced ? "text-emerald-400" : over ? "text-red-400" : "text-amber-400"}`}>
          {balanced && "✓ Heads balanced"}
          {over && `Over by ${sumHeads - totalHeads} heads`}
          {under && `Short by ${totalHeads - sumHeads} heads`}
        </div>
      </div>
      {rows.length >= orderArticles.length && (
        <div className="mt-1 text-[10px] text-amber-400/80 flex items-start gap-1.5" data-testid={`planner-split-hint-${machine.code}`}>
          <WarningCircleIcon size={11} weight="fill" className="text-amber-400 flex-shrink-0 mt-0.5" />
          <span>Attach another compatible Work Order at the top ("Additional WOs · Split-Head") to unlock more variants.</span>
        </div>
      )}
    </div>
  );
}
